import React from 'react'
import { makeAutoObservable, reaction, toJS } from 'mobx'
import * as R from 'ramda'
import i18n from 'i18next'
import { v4 as uuidv4 } from 'uuid'
import { capitalize, cloneDeep, startCase } from 'lodash'
import {
    Line, Pie, QuadrantAnalysis,
} from 'frontend-charts'

import RuleGroup from '../ReportV2/components/Modal/Filter/model/RuleGroup'
import Rule from '../ReportV2/components/Modal/Filter/model/Rule'
import DataFormatter from './dataFormatter'
import buildSkipLogicAdapter from './skipLogicAdapter'
import { getQuestionTitle, getOptionTitle } from '../../utils/QuestionHelper'
import PPPresentation from './classes/Presentation'
import PPSection from './classes/Section'
import PPSlide from './classes/Slide'
import PPQuestion from './classes/Question'
import LMX from '../../Charts/LMX'
import Bar from '../../Charts/Bar'

import ENUM from '../PresentationV2/ENUM'
import { isEmpty, stripOutHtml } from '../helpers'
import chartRenderSettings from './chartRenderSettings'
import colours from '../ReportV2/components/RightPanel/Chart/colours'
import { sortBy, sortedIndexArray } from '../../utils/helpers'
import MergeFields from '../../utils/MergeFields'
import { MIXPANEL_EVENTS } from '../../utils/MixpanelManager'
import { toReportPage } from '../ReportV2/Model/ReportPage'
import { mapToQuestion } from '../ReportV2/Model/ReportQuestion'

const BUILDING_MIN_DURATION_MS = 3200

const yearColourKey = (year, surveyYear) => {
    const diff = Number(surveyYear) - Number(year)
    if (!Number.isFinite(diff) || diff === 0) return 'year'
    return diff > 0 ? `year-${diff}` : `year+${-diff}`
}

const HISTORY_LIMIT = 10

const SAVE_DEBOUNCE_MS = 5000

const SAVE_TICK_MS = 30 * 1000

const DOWNLOAD_STEP_MS = 1200
const DOWNLOAD_STEPS = 3
const DOWNLOAD_MIN_DURATION_MS = DOWNLOAD_STEP_MS * DOWNLOAD_STEPS

const NON_DUPLICABLE_SLIDE_TEMPLATES = ['title', 'sectionHeader', 'summary', 'sectionSummary']

const NO_QUESTION_SLIDE_TEMPLATES = ['title', 'sectionHeader', 'specification', 'swot', 'summary', 'sectionSummary', 'recommendations']

const normalizeStackedData = (seriesList) => {
    if (!Array.isArray(seriesList) || seriesList.length <= 1) return seriesList
    const positions = seriesList.reduce((max, s) => Math.max(max, s.data?.length ?? 0), 0)
    const totals = Array.from({ length: positions }, (_, j) => (
        seriesList.reduce((sum, s) => sum + (Number(R.pathOr(0, ['data', j, 'value'], s)) || 0), 0)
    ))
    return seriesList.map((s) => ({
        ...s,
        data: (s.data ?? []).map((d, j) => ({
            ...d,
            value: totals[j] ? ((Number(d.value) || 0) / totals[j]) * 100 : 0,
        })),
    }))
}

let allSurveysCache = null
let allSurveysInflight = null

const settingsDefaults = {
    sorting: 'noRanking',
    removeZero: false,
    removeBaseLimit: 0,
    excludeNonNumeric: false,
    showAverage: false,
    optionsDisplayed: null,
}

const filterDefaults = {
    state: [],
    surveyDataIds: [],
    question: {},
    campaign: {},
    responseDate: {},
}

export default class PowerpointBuilderStore {
    slideSelectModalOpen = false

    filterModalOpen = false

    verbatimSettingsModalVisible = false

    globalSettingsModalOpen = false

    slideSettingsModalOpen = false

    chartSettingsModalOpen = false

    derivedChartModalOpen = false

    inheritedSettingsModalVisible = false

    lmxSettingsModalVisible = false

    crossbreakSettingsModalVisible = false

    slidePreviewCollapsed = false

    deletePresentationModalVisible = false

    undoStack = []

    redoStack = []

    applyingHistory = false

    chartSettingsBaseline = null

    saveTimer = null

    presentationToDelete = null

    saving = false

    lastSavedAt = null

    nowTick = Date.now()

    downloadModalState = 'idle'

    downloadWarningOpen = false

    downloadStepIndex = 0

    downloadFile = null

    downloadError = null

    sectionTitleModalVisible = false

    surveys = []

    survey = null

    fullSurveys = new Map()

    skipLogicAdapters = new Map()

    search = ''

    presentationsSearch = ''

    presentationsEventFilter = null

    questions = []

    filteredQuestions = []

    benchmarks = new Map()

    showAverageByQuestionId = new Map()

    lmxBenchmarkData = new Map()

    sideNavigationOpen = ''

    currentSection = 0

    currentSlide = 0

    editingSection = null

    currentChart = null

    verbatimPosition = 0

    verbatimSlots = 0

    addPosition = null

    globalYears = []

    globalBenchmarks = []

    questionData = new Map()

    chartData = new Map()

    benchmarkPeriod = 'ROL-36'

    questionCodingAvailability = new Map()

    surveyCodingAvailability = new Map()

    editing = false

    defaultLabelPositions = {
        bar: 'right',
        default: 'top',
        pie: 'outside',
        doughnut: 'outside',
    }

    derivedChartModalSelections = {
        question: '',
        kpi: '',
        base: 0,
    }

    inheritedChartModalSelections = {
        parent: '',
        child: '',
        ranking: 'highToLow',
        base: 0,
        average: 'average',
        removeZero: true,
        locked: true,
        chartType: 'standardColumn',
        comparison: [],
        comparisonYears: [],
    }

    crossbreakChartModalSelections = {
        questions: [
            {
                question: '',
                ranking: 'noRanking',
                base: 0,
            },
        ],
        kpi: '',
        benchmarks: [],
    }

    questionBenchmarks = new Map()

    questionYears = new Map()

    product = {
        name: '',
        edition: '',
    }

    kpis = {
        OS: false,
        FA: false,
        NPS: false,
        IOV: false,
    }

    oidMap = {
        industry: 1,
        company: 3,
        division: 4,
        portfolio: 5,
        sector: 1,
    }

    possibleBenchmarks = {
        3: ['industry', 'client'],
        4: ['industry', 'client', 'division'],
        5: ['industry', 'client', 'division', 'portfolio'],
        6: ['industry', 'client', 'division', 'portfolio', 'sector'],
    }

    chartFilterOptions = [
        { label: 'No ranking', value: 'noRanking' },
        { label: 'Low to high', value: 'lowToHigh' },
        { label: 'High to low', value: 'highToLow' },
        { label: 'A to Z', value: 'aToZ' },
        { label: 'Z to A', value: 'zToA' },
    ]

    chartTypeOptions = [
        {
            label: 'Verbatim',
            value: 'verbatim',
            filter: (questionType, kpiType) => (
                ['TA', 'TX'].includes(questionType)
            ),
        },
        {
            label: 'NPS chart',
            value: 'npsChart',
            filter: (questionType, kpiType) => (
                kpiType === 'NPS'
            ),
        },
        {
            label: 'KPI chart',
            value: 'kpiChart',
            filter: (questionType, kpiType, hasNumericCodes) => (
                (questionType === 'RD' && hasNumericCodes) && (kpiType !== 'NPS')
            ),
        },
        {
            label: 'Column chart',
            value: 'standardColumn',
            filter: (questionType) => (
                ['OD', 'RD', 'CH', 'SB', 'PC'].includes(questionType)
            ),
        },
        {
            label: 'Bar chart',
            value: 'standardBar',
            filter: (questionType, kpiType) => (
                ['OD', 'RD', 'CH', 'SB', 'PC'].includes(questionType) && kpiType !== 'NPS'
            ),
        },
        {
            label: 'Pie chart',
            value: 'pie',
            filter: (questionType, kpiType) => (
                ['RD', 'SB'].includes(questionType) && kpiType !== 'NPS'
            ),
        },
        {
            label: 'Donut chart',
            value: 'doughnut',
            filter: (questionType, kpiType) => (
                ['RD', 'SB'].includes(questionType) && kpiType !== 'NPS'
            ),
        },
        {
            label: 'Stacked bar',
            value: 'stackedBar',
            filter: (questionType, kpiType) => (
                ['TB', 'SG'].includes(questionType)
            ),
        },
        {
            label: 'Stacked column',
            value: 'stackedColumn',
            filter: (questionType, kpiType) => (
                ['TB', 'SG'].includes(questionType)
            ),
        },
    ]

    benchmarkPeriods = [
        {
            value: 'ROL-36',
            label: '36 Months',
            benchmarkType: 'ROL',
            period: { periodType: 'ROL', duration: 36 },
            ossPeriod: { periodType: 'last', interval: 'MONTH', duration: 36 },
        },
        {
            value: 'ROL-84',
            label: 'Long-term',
            benchmarkType: 'ROL',
            period: { periodType: 'ROL', duration: 84 },
            ossPeriod: { periodType: 'last', interval: 'MONTH', duration: 84 },
        },
    ]

    benchmarkOptions = {
        company: false,
        division: false,
        industry: false,
        portfolio: false,
        sector: false,
    }

    filters = {
        question: new RuleGroup(RuleGroup.getDefaultLogic('question')),
        campaign: new RuleGroup(RuleGroup.getDefaultLogic('campaign')),
        responseDate: new RuleGroup(RuleGroup.getDefaultLogic('responseDate')),
        openEnded: new RuleGroup(RuleGroup.getDefaultLogic('question')),
    }

    surveyFilterQuestions = new Map()

    openFilterPopoverPosition = null

    presentation = null

    surveyYear = null

    stage = 'landing'

    isTemplate = false

    templateId = null

    templateName = ''

    templateSurveyType = null

    templateRespondentType = null

    templateProductType = null

    selectedTemplateEvent = null

    templateEventLoading = false

    templateSettingsModalOpen = false

    insertTemplateModalOpen = false

    insertableTemplates = []

    insertableTemplatesLoaded = false

    selectedInsertTemplate = null

    insertTemplateMissingQuestions = []

    events = []

    allSurveys = []

    event = null

    entrySurvey = null

    entryTitle = ''

    presentations = []

    filteredPresentations = []

    presentationsLoading = true

    pendingPresentationData = null

    specificationData = null

    buildingError = null

    newPresentationId = null

    startingSurveyId = null

    constructor(store) {
        this.appStore = store
        makeAutoObservable(this)
        setInterval(() => {
            this.nowTick = Date.now()
        }, SAVE_TICK_MS)
        reaction(
            () => this.specificationSignature,
            (signature) => { if (signature) this.fetchSpecificationData() },
            { delay: 400 },
        )
    }

    get presentationContext() {
        const sections = this.presentation?.section ?? []
        const slideCount = sections.reduce((acc, s) => acc + (s.slide?.length ?? 0), 0)
        return {
            presentation_id: this.presentation?.id,
            presentation_title: this.presentation?.title,
            survey_id: this.presentation?.surveyId,
            section_count: sections.length,
            slide_count: slideCount,
            is_template: this.isTemplate,
        }
    }

    trackPresentation = (eventName, properties = {}) => {
        this.appStore?.mixpanel?.track(eventName, { ...this.presentationContext, ...properties })
    }

    get specificationSignature() {
        const sections = R.pathOr([], ['presentation', 'section'], this)
        const hasSpecificationSlide = sections
            .some((section) => R.pathOr([], ['slide'], section).some((slide) => slide.template === 'specification'))
        if (!hasSpecificationSlide) return ''

        const surveyIds = new Set()
        const comparisonIds = new Set()
        sections.forEach((section) => section.slide.forEach((slide) => slide.question.forEach((question) => {
            if (question.surveyId) surveyIds.add(question.surveyId)
            const comparisons = question.comparison || []
            comparisons.forEach((id) => { if (id) comparisonIds.add(id) })
        })))

        return JSON.stringify({
            surveyId: Array.from(surveyIds).sort(),
            comparisonId: Array.from(comparisonIds).sort(),
        })
    }

    get anyModalOpen() {
        return [
            this.slideSelectModalOpen,
            this.filterModalOpen,
            this.verbatimSettingsModalVisible,
            this.globalSettingsModalOpen,
            this.slideSettingsModalOpen,
            this.chartSettingsModalOpen,
            this.derivedChartModalOpen,
            this.inheritedSettingsModalVisible,
            this.lmxSettingsModalVisible,
            this.crossbreakSettingsModalVisible,
            this.deletePresentationModalVisible,
        ].includes(true)
    }

    startBuilder = () => {
        this.preloadBuilder()
        this.stage = 'builder'
    }

    startNewPresentation = (id, event, entrySurvey, entryTitle, startingSurveyId) => {
        this.newPresentationId = id
        if (event !== undefined) this.event = event
        if (event !== undefined) this.presentationsEventFilter = event
        if (entrySurvey !== undefined) this.entrySurvey = entrySurvey
        if (entryTitle !== undefined) this.entryTitle = entryTitle
        if (startingSurveyId !== undefined) this.startingSurveyId = startingSurveyId
        this.startBuilding()
    }

    preloadBuilder = async () => {
        let hydrated = false
        if (this.pendingPresentationData) {
            const id = this.pendingPresentationData.id ?? this.newPresentationId ?? uuidv4()
            const data = this.pendingPresentationData.id ? this.pendingPresentationData : this.buildBlankTemplateData(this.startingSurveyId)
            this.presentation = this.hydratePresentation(data)
            this.pendingPresentationData = null
            hydrated = true
        } else {
            const id = this.newPresentationId ?? uuidv4()
            this.newPresentationId = null
            this.presentation = new PPPresentation({
                id,
                clientName: this.appStore.userData.clientName,
                locale: null,
                title: this.entryTitle ?? '',
                benchmarkPeriod: this.benchmarkPeriods.find(({ value }) => value === this.benchmarkPeriod)?.period,
                section: [
                    this.isTemplate
                        ? {
                            title: 'Section 1',
                            slide: [
                                {
                                    title: '',
                                    template: '1x1',
                                },
                            ],
                        }
                        : {
                            title: 'Intro',
                            slide: [
                                {
                                    title: 'Title Slide',
                                    template: 'title',
                                },
                            ],
                        },
                ],
            }, this.appStore)
        }

        await this.init()

        if (hydrated) {
            await this.fetchHydratedQuestionData()
        }

        if (!this.isTemplate) this.getInsertableTemplates()
    }

    fetchHydratedQuestionData = async () => {
        const bySurvey = new Map()
        const verbatimCharts = []

        const bucketFor = (surveyId) => {
            const key = surveyId?.toUpperCase?.() ?? surveyId
            if (!bySurvey.has(key)) {
                bySurvey.set(key, {
                    filterByQuestionId: new Map(),
                    crossbreakCharts: [],
                    derivedCharts: [],
                    inheritedCharts: [],
                    needsLMX: false,
                    needsOSS: false,
                })
            }
            return bySurvey.get(key)
        }

        this.presentation.section.forEach((section) => {
            section.slide.forEach((slide) => {
                const { template } = slide
                slide.question.forEach((ppQuestion) => {
                    if (ppQuestion.isVerbatim) {
                        verbatimCharts.push(ppQuestion)
                        return
                    }
                    const bucket = bucketFor(ppQuestion.surveyId)
                    if (template === 'crosstab') {
                        bucket.crossbreakCharts.push(ppQuestion)
                        return
                    }
                    if (template === 'derived') {
                        bucket.derivedCharts.push(ppQuestion)
                        return
                    }
                    if (template === 'inherited') {
                        bucket.inheritedCharts.push(ppQuestion)
                        return
                    }
                    if (template === 'lmx') {
                        bucket.needsLMX = true
                        return
                    }
                    if (template === 'oss') {
                        bucket.needsOSS = true
                        return
                    }

                    const { questionId } = ppQuestion
                    if (!questionId) return
                    if (bucket.filterByQuestionId.has(questionId)) return
                    bucket.filterByQuestionId.set(questionId, ppQuestion.filter)
                })
            })
        })

        const originalSurvey = this.survey

        await Array.from(bySurvey.entries()).reduce(async (previous, [surveyKey, bucket]) => {
            await previous

            const targetSurvey = this.surveys.find((s) => s.surveyId?.toUpperCase?.() === surveyKey)
            if (!targetSurvey) return undefined

            if (this.survey?.surveyId !== targetSurvey.surveyId) {
                await this.handleSelectSurvey({ value: targetSurvey.surveyId })
            }

            const {
                filterByQuestionId, crossbreakCharts, derivedCharts, inheritedCharts,
                needsLMX, needsOSS,
            } = bucket

            if (filterByQuestionId.size > 0) {
                const matched = this.questions
                    .filter(({ question }) => filterByQuestionId.has(question.id))
                    .map((q) => ({ ...q, filter: filterByQuestionId.get(q.question.id) }))
                if (matched.length > 0) await this.getQuestionData(matched)
            }

            const seenCrossbreak = new Set()
            await Promise.all(crossbreakCharts
                .filter((ppQ) => {
                    if (!ppQ.questionId || seenCrossbreak.has(ppQ.questionId)) return false
                    seenCrossbreak.add(ppQ.questionId)
                    return true
                })
                .map((ppQ) => this.getCrossTabChartData(ppQ)))

            const seenDerived = new Set()
            await Promise.all(derivedCharts
                .filter((ppQ) => {
                    const key = `${ppQ.questionId}-${ppQ.kpiQuestionId}`
                    if (!ppQ.questionId || seenDerived.has(key)) return false
                    seenDerived.add(key)
                    return true
                })
                .map((ppQ) => this.getDerivedChartData(ppQ)))

            const seenInherited = new Set()
            await Promise.all(inheritedCharts
                .filter((ppQ) => {
                    if (!ppQ.questionId || seenInherited.has(ppQ.questionId)) return false
                    seenInherited.add(ppQ.questionId)
                    return true
                })
                .map((ppQ) => {
                    const parent = this.questions.find((q) => q.question.id === ppQ.questionId)
                    const childId = ppQ.questions?.[0]?.questionId
                    const child = childId
                        ? this.questions.find((q) => q.question.id === childId)
                        : undefined
                    if (!parent || !child) return null
                    return this.getInheritedChartData(parent, child)
                })
                .filter(Boolean))

            if (needsLMX) await this.fetchLMXChartData()
            if (needsOSS) await this.fetchOSSChartData()

            return undefined
        }, Promise.resolve())

        if (originalSurvey && this.survey?.surveyId !== originalSurvey.surveyId) {
            await this.handleSelectSurvey({ value: originalSurvey.surveyId })
        }

        if (verbatimCharts.length > 0) {
            await Promise.all(verbatimCharts.map((ppQ) => ppQ.fetchData()))
        }
    }

    hydratePresentation = (data) => {
        const presentation = new PPPresentation({
            id: data.id,
            locale: data.locale,
            clientName: data.clientName,
            title: data.title,
            benchmarkPeriod: data.benchmarkPeriod,
            section: [],
        }, this.appStore)

        if (Array.isArray(data.surveyId)) presentation.surveyId = data.surveyId
        if (Array.isArray(data.benchmarks)) presentation.benchmarks = data.benchmarks

        if (this.isTemplate && data.title) {
            this.templateName = data.title
            this.entryTitle = data.title
        }

        presentation.section = (data.section ?? []).map((secData) => {
            const section = new PPSection({ title: secData.title })
            if (secData.id) section.id = secData.id
            section.slide = (secData.slide ?? []).map((slideData) => {
                const slide = new PPSlide({
                    title: slideData.title,
                    hasCustomTitle: slideData.hasCustomTitle,
                    template: slideData.template,
                })
                if (slideData.id) slide.id = slideData.id;
                (slideData.question ?? []).forEach((qData, idx) => {
                    let { comparisonYears } = qData
                    if (
                        (!Array.isArray(comparisonYears) || comparisonYears.length === 0)
                        && Array.isArray(qData.comparison)
                        && qData.comparison.length > 0
                    ) {
                        const yearSet = new Set()
                        qData.comparison.forEach((sid) => {
                            const match = this.allSurveys.find((s) => (
                                s.surveyId?.toUpperCase() === String(sid).toUpperCase()
                            ))
                            if (match?.year != null) yearSet.add(Number(match.year))
                        })
                        comparisonYears = Array.from(yearSet)
                    }

                    const question = new PPQuestion({
                        ...qData,
                        surveyId: this.isTemplate ? (this.startingSurveyId?.toUpperCase() ?? qData.surveyId) : qData.surveyId,
                        year: this.isTemplate ? (this.activeSurvey?.getYear() ?? qData.year) : qData.year,
                        kpiType: qData.chart === 'npsChart' ? 'NPS' : qData.kpiType,
                        comparisonYears,
                        ppStore: this,
                        store: this.appStore,
                    })
                    slide.question.set(qData.position ?? idx, question)
                })
                return slide
            })
            return section
        })

        return presentation
    }

    openPresentation = (presentation) => {
        const data = presentation?.data
        if (!data) return
        this.startingSurveyId = data.surveyId?.[0] ?? null
        this.pendingPresentationData = data
        this.startBuilding()
    }

    fetchTemplateById = async (id) => {
        try {
            const template = await this.appStore.http.get(`/export/ppt/template/${id}`)
            return template ?? null
        } catch (e) {
            console.error('Failed to fetch presentation template', e)
            return null
        }
    }

    buildBlankTemplateData = (startingSurveyId) => ({
        id: this.newPresentationId ?? uuidv4(),
        clientName: this.appStore.userData.clientName,
        locale: null,
        title: this.templateName || '',
        surveyId: startingSurveyId ? [startingSurveyId] : [],
        section: [
            {
                title: 'Section 1',
                slide: [
                    {
                        title: '',
                        template: '1x1',
                    },
                ],
            },
        ],
    })

    openTemplate = ({ data, startingSurveyId }) => {
        this.startingSurveyId = startingSurveyId ?? null
        this.entryTitle = this.templateName || ''
        this.selectedTemplateEvent = null
        this.pendingPresentationData = data || this.buildBlankTemplateData(startingSurveyId)
        this.startBuilding()
    }

    openTemplateSettingsModal = () => { this.templateSettingsModalOpen = true }

    closeTemplateSettingsModal = () => { this.templateSettingsModalOpen = false }

    setTemplateName = (e) => {
        this.templateName = e.value
        this.entryTitle = e.value
        if (this.presentation) this.presentation.title = e.value
    }

    get templateEventOptions() {
        const surveyType = this.templateSurveyType ?? this.survey?.surveyType
        const respondentType = this.templateRespondentType ?? this.survey?.respondentType
        const events = new Map()
        this.allSurveys
            .filter((s) => s.surveyType === surveyType && s.respondentType === respondentType)
            .forEach((s) => {
                if (!events.has(s.productId)) events.set(s.productId, s.productName)
            })
        return Array.from(events.entries()).map(([value, label]) => ({ value, label }))
    }

    get currentTemplateEvent() {
        return this.selectedTemplateEvent ?? this.survey?.productId ?? null
    }

    resolveTemplateExampleSurvey = (productId) => {
        const surveyType = this.templateSurveyType ?? this.survey?.surveyType
        const respondentType = this.templateRespondentType ?? this.survey?.respondentType
        return this.allSurveys
            .filter((s) => (
                s.surveyType === surveyType
                && s.respondentType === respondentType
                && s.productId === productId
            ))
            .sort((a, b) => b.year - a.year)[0]
    }

    handleSelectTemplateEvent = async (e) => {
        const productId = e?.value
        const example = this.resolveTemplateExampleSurvey(productId)
        if (!example || example.surveyId?.toUpperCase() === this.startingSurveyId?.toUpperCase()) return

        this.templateEventLoading = true
        try {
            this.startingSurveyId = example.surveyId
            this.selectedTemplateEvent = productId

            await this.getSurveys()
            await this.handleSelectSurvey({ value: example.surveyId.toUpperCase() })

            const surveyIdUpper = example.surveyId.toUpperCase()
            const year = this.activeSurvey?.getYear()
            this.presentation.section.forEach((section) => {
                section.slide.forEach((slide) => {
                    slide.question.forEach((question) => {
                        question.surveyId = surveyIdUpper
                        question.year = year
                        question.comparison = []
                        question.comparisonYears = []
                    })
                })
            })

            await this.fetchHydratedQuestionData()
        } catch (err) {
            console.error('Failed to swap template event', err)
        } finally {
            this.templateEventLoading = false
        }
    }

    stripTemplateData = () => {
        const data = JSON.parse(JSON.stringify(this.presentation))
        data.benchmarkPeriod = { periodType: '', duration: 0 }
        data.surveyId = []
        R.pathOr([], ['section'], data).forEach((section) => {
            R.pathOr([], ['slide'], section).forEach((slide) => {
                R.pathOr([], ['question'], slide).forEach((question) => {
                    question.benchmark = []
                    question.comparison = []
                    question.comparisonYears = []
                    question.pageQuestionId = ''
                    question.surveyId = ''
                })
            })
        })
        return data
    }

    saveTemplate = async () => {
        if (!this.templateId) return
        this.saving = true
        try {
            await this.appStore.http.post(`/export/ppt/template/${this.templateId}`, JSON.stringify({
                title: this.templateName,
                data: JSON.stringify(this.stripTemplateData()),
            }))
            this.lastSavedAt = Date.now()
            this.nowTick = Date.now()
            this.appStore.addNotification(null, i18n.t('templates.saveTemplate.success'), 'success')
        } catch (e) {
            console.error('Failed to save presentation template', e)
            this.appStore.addNotification(null, i18n.t('templates.saveTemplate.error'), 'error')
        } finally {
            this.saving = false
        }
    }

    isRealTemplateQuestion = (q) => (
        !!q.questionId
        && q.questionId !== '00000000-0000-0000-0000-000000000000'
        && !['LMX', 'OSS'].includes(q.kpiType)
    )

    get currentSurveyQuestionIds() {
        return new Set(this.questions.map((q) => String(R.pathOr('', ['question', 'id'], q)).toUpperCase()))
    }

    slideFits = (slide) => {
        const idSet = this.currentSurveyQuestionIds
        return R.pathOr([], ['question'], slide)
            .filter(this.isRealTemplateQuestion)
            .every((q) => idSet.has(String(q.questionId).toUpperCase()))
    }

    getInsertableTemplates = async () => {
        try {
            const templates = await this.appStore.http.get('/export/ppt/templates')
            const list = Array.isArray(templates) ? templates : []
            this.insertableTemplates = list.filter((t) => (
                t.surveyType === this.survey?.surveyType
                && t.respondentType === this.survey?.respondentType
                && t.productType === this.survey?.producttypecode
            ))
        } catch (e) {
            console.error('Failed to load presentation templates', e)
            this.insertableTemplates = []
        } finally {
            this.insertableTemplatesLoaded = true
        }
    }

    get canInsertTemplate() {
        return this.insertableTemplates.length > 0
    }

    getRespondentTypeLabel = (type) => {
        const keyMap = {
            EX: 'model.services.dashboardService.generateComment.respondenttype.EX',
            VI: 'model.services.dashboardService.generateComment.respondenttype.VI',
            NN: 'model.services.companyTableService.companyTable.filters.benchmarks.none',
            PR: 'refcodes_PRNA',
            RD: 'templates.reader',
        }
        return keyMap[type] ? i18n.t(keyMap[type]) : (type || '')
    }

    get insertTemplateTooltip() {
        const event = this.survey?.productName || this.survey?.producttypecode || ''
        const respondent = this.getRespondentTypeLabel(this.survey?.respondentType)
        return i18n.t('templates.insertUnavailable', { event, respondent })
    }

    openInsertTemplateModal = async () => {
        this.selectedInsertTemplate = null
        this.insertTemplateMissingQuestions = []
        this.insertTemplateModalOpen = true
        await this.getInsertableTemplates()
    }

    closeInsertTemplateModal = () => {
        this.insertTemplateModalOpen = false
        this.selectedInsertTemplate = null
        this.insertTemplateMissingQuestions = []
    }

    clearInsertTemplateSelection = () => {
        this.selectedInsertTemplate = null
        this.insertTemplateMissingQuestions = []
    }

    getTemplateMissingQuestions = (data) => {
        const idSet = this.currentSurveyQuestionIds
        const missing = new Map()
        R.pathOr([], ['section'], data).forEach((section) => {
            R.pathOr([], ['slide'], section).forEach((slide) => {
                R.pathOr([], ['question'], slide)
                    .filter(this.isRealTemplateQuestion)
                    .forEach((q) => {
                        const qid = String(q.questionId).toUpperCase()
                        if (!idSet.has(qid)) missing.set(qid, q.questionShortText || q.questionId)
                    })
            })
        })
        return Array.from(missing.values())
    }

    getTemplateOmittedSlideCount = (data) => (
        R.pathOr([], ['section'], data).reduce((total, section) => (
            total + R.pathOr([], ['slide'], section).filter((slide) => !this.slideFits(slide)).length
        ), 0)
    )

    selectInsertTemplate = async (id) => {
        try {
            const template = await this.appStore.http.get(`/export/ppt/template/${id}`)
            const data = typeof template.data === 'string' ? JSON.parse(template.data) : template.data
            const missing = this.getTemplateMissingQuestions(data)
            const omitted = this.getTemplateOmittedSlideCount(data)

            if (!missing.length && !omitted) {
                this.insertTemplateData(data)
                return
            }

            this.selectedInsertTemplate = { ...template, data: data || null }
            this.insertTemplateMissingQuestions = missing
        } catch (e) {
            console.error('Failed to load presentation template', e)
        }
    }

    confirmInsertTemplate = () => {
        this.insertTemplateData(R.pathOr(null, ['selectedInsertTemplate', 'data'], this))
    }

    insertTemplateData = (data) => {
        if (!data) return
        this.recordHistory()
        let insertedSlideCount = 0
        const surveyId = this.survey?.surveyId
        const insertedIds = new Set()
        let insertedLMX = false
        let insertedOSS = false

        const surveyQuestionById = new Map(
            this.questions.map((q) => [
                String(R.pathOr('', ['question', 'id'], q)).toUpperCase(),
                q,
            ])
        )

        R.pathOr([], ['section'], data).forEach((secData) => {
            const fittingSlides = R.pathOr([], ['slide'], secData).filter((slide) => this.slideFits(slide))
            if (!fittingSlides.length) return
            insertedSlideCount += fittingSlides.length
            const section = new PPSection({ title: secData.title })
            section.slide = fittingSlides.map((slideData) => {
                const slide = new PPSlide({
                    title: slideData.title,
                    hasCustomTitle: slideData.hasCustomTitle,
                    template: slideData.template,
                })
                R.pathOr([], ['question'], slideData).forEach((qData, idx) => {
                    const matchedSurveyQuestion = surveyQuestionById.get(String(qData.questionId).toUpperCase())
                    const resolvedPageQuestionId = R.pathOr(qData.pageQuestionId || '', ['pageQuestionId'], matchedSurveyQuestion)
                    const defaults = (this.isRealTemplateQuestion(qData) && matchedSurveyQuestion)
                        ? this.mapQuestionToChartDefaults(matchedSurveyQuestion)
                        : null
                    const question = new PPQuestion({
                        ...qData,
                        surveyId,
                        year: this.survey?.year ?? qData.year,
                        pageQuestionId: resolvedPageQuestionId.toLowerCase(),
                        kpiType: qData.chart === 'npsChart' ? 'NPS' : qData.kpiType,
                        benchmark: defaults?.benchmark ?? [],
                        comparison: defaults?.comparison ?? [],
                        comparisonYears: defaults?.comparisonYears ?? [],
                        ppStore: this,
                        store: this.appStore,
                    })
                    slide.question.set(qData.position ?? idx, question)
                    if (this.isRealTemplateQuestion(qData)) insertedIds.add(String(qData.questionId).toUpperCase())
                    if (qData.kpiType === 'LMX') insertedLMX = true
                    if (qData.kpiType === 'OSS') insertedOSS = true
                })
                return slide
            })
            this.presentation.section.push(section)
        })

        const matched = this.questions
            .filter((q) => insertedIds.has(String(R.pathOr('', ['question', 'id'], q)).toUpperCase()))
            .map((q) => ({ ...q }))
        if (matched.length) this.getQuestionData(matched)
        if (insertedLMX) this.fetchLMXChartData()
        if (insertedOSS) this.fetchOSSChartData()

        if (insertedSlideCount) {
            this.trackPresentation(MIXPANEL_EVENTS.PRESENTATION_TEMPLATE_INSERTED, {
                template_id: data?.id,
                template_title: data?.title,
                inserted_slide_count: insertedSlideCount,
            })
            this.appStore.addNotification(
                null,
                i18n.t(
                    insertedSlideCount === 1
                        ? 'templates.insertModal.insertSuccessOne'
                        : 'templates.insertModal.insertSuccess',
                    { count: insertedSlideCount },
                ),
                'success',
            )
        }

        this.closeInsertTemplateModal()
        this.scheduleSave()
    }

    initPresentations = async () => {
        await Promise.all([
            this.getEvents(),
            this.getAllSurveys(),
        ])
        await this.getPresentations()
    }

    getEvents = async () => {
        const productResponse = await this.appStore.http.get('/product')
        this.events = productResponse.map((product) => ({
            productId: product.productId,
            portfolioId: product.portfolioId,
            productType: product.productType,
            noSurveys: product.numberOfSurveys,
            name: `${product.name}${product.edition ? ` - ${product.edition}` : ''}`,
            status: product.status,
        }))
    }

    getAllSurveys = async () => {
        if (allSurveysCache) {
            this.allSurveys = allSurveysCache
            return
        }
        if (!allSurveysInflight) {
            allSurveysInflight = Promise.resolve(this.appStore.http.get('/survey'))
                .then((res) => {
                    allSurveysCache = res ?? []
                    allSurveysInflight = null
                    return allSurveysCache
                })
                .catch((e) => {
                    allSurveysInflight = null
                    throw e
                })
        }
        this.allSurveys = await allSurveysInflight
    }

    fetchPresentationById = async (id) => {
        try {
            const presentation = await this.appStore.http.get(`/export/ppt/${id}`)
            return presentation ?? null
        } catch (e) {
            console.error('Failed to fetch presentation', e)
            return null
        }
    }

    getPresentations = async () => {
        this.presentationsLoading = true
        try {
            const list = await this.appStore.http.get('/export/ppt/list')
            this.presentations = Array.isArray(list)
                ? list.map((p) => ({ ...p, data: JSON.parse(p.data) })).sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated))
                : []

            this.filterPresentations()
        } catch (e) {
            console.error('Failed to load presentations list', e)
            this.presentations = []
        } finally {
            this.presentationsLoading = false
        }
    }

    filterPresentations = () => {
        let preFiltered = [...this.presentations.map((p) => {
            const fullSurvey = this.allSurveys.find((survey) => survey.surveyId === p.data.surveyId[0])
            return { ...p, eventName: fullSurvey?.productName ?? null }
        })]

        if (this.presentationsEventFilter) {
            const filteredSurveyIds = this.allSurveys
                .filter((surv) => surv.productId === this.presentationsEventFilter)
                .map((sur) => sur.surveyId)

            preFiltered = preFiltered.filter((pp) => pp.data.surveyId.some((sid) => filteredSurveyIds.includes(sid)))
        }

        if (this.presentationsSearch.length) {
            preFiltered = preFiltered
                .filter((pp) => pp.data.title.toLocaleLowerCase()
                    .includes(this.presentationsSearch.toLocaleLowerCase()))
        }

        this.filteredPresentations = preFiltered
    }

    handlePresentationsEventFilter = (e) => {
        this.presentationsEventFilter = e.value
        this.filterPresentations()
    }

    handlePresentationsFilter = (e) => {
        this.presentationsSearch = e.value
        this.filterPresentations()
    }

    get clientCodingAvailability() {
        const { userData } = this.appStore
        return R.pathOr(false, ['flags', 'isAiSummaryEnabled'], userData)
    }

    get presentationCodingAvailability() {
        if (!this.clientCodingAvailability) return 'client'
        if (!this.surveyCodingAvailability.get(this.activeChart?.surveyId.toUpperCase())) return 'survey'
        return null
    }

    get isGESurvey() {
        return this.survey?.surveyType === 'GE'
    }

    get reportScope() {
        return this.isGESurvey ? 'survey' : 'product'
    }

    get reportScopeFields() {
        return this.isGESurvey
            ? { scope: 'survey', surveyId: this.survey.surveyId }
            : { scope: 'product' }
    }

    get eventOptions() {
        return this.events.map((event) => ({
            value: event.productId,
            label: `${event.name} (${event.noSurveys} Surveys)`,
        }))
    }

    get activeSurvey() {
        if (this.startingSurveyId) {
            const entSurv = this.allSurveys
                .find((s) => s.surveyId === this.startingSurveyId.toUpperCase())

            return {
                id: entSurv?.surveyId,
                surveyName: entSurv?.surveyName,
                productName: entSurv?.productName,
                metadata: {
                    productInfo: {
                        productId: entSurv?.productId,
                        industrySpecificInfo: {
                            productType: entSurv?.producttypecode,
                            industry: entSurv?.industry,
                        },
                    },
                    taxonomy: { surveyType: entSurv?.surveyType },
                },
                getYear: () => entSurv?.year,
            }
        }

        if (this.entrySurvey) {
            const productSurveys = this.allSurveys
                .filter((s) => s.productId === this.event && s.surveyType === this.entrySurvey)
            productSurveys.sort((a, b) => b.year - a.year)
            const entSurv = productSurveys[0]

            return {
                id: entSurv?.surveyId,
                surveyName: entSurv?.surveyName,
                productName: entSurv?.productName,
                metadata: {
                    productInfo: {
                        productId: entSurv?.productId,
                        industrySpecificInfo: {
                            productType: entSurv?.producttypecode,
                            industry: entSurv?.industry,
                        },
                    },
                    taxonomy: { surveyType: entSurv?.surveyType },
                },
                getYear: () => entSurv?.year,
            }
        }

        return this.appStore.survey
    }

    get entrySurveyOptions() {
        const surveyTypes = new Set()
        this.allSurveys
            .filter((survey) => survey.productId === this.event)
            .map((surv) => surveyTypes.add(surv.surveyType))
        return Array.from(surveyTypes).map((s) => ({
            value: s,
            label: this.getSurveyType(s),
        }))
    }

    get slideCount() {
        return this.presentation.section.map((s) => s.slide.length).reduce((a, b) => a + b, 0)
    }

    get currentSlideIndex() {
        const idArr = this.presentation.section.map((s) => s.slide.map((sl) => sl.id)).flat()
        return idArr.indexOf(this.activeSlide?.id) + 1
    }

    get presentationTitle() {
        return this.presentation?.title ?? ''
    }

    getSurveyType = (type) => {
        switch (type) {
            case 'PE':
                return i18n.t('surveyType_Benchmarking')
            case 'GE':
                return i18n.t('surveyType_General')
            case 'EM':
                return i18n.t('navmenu.maxbi')
            case 'CA':
                return 'Tracker'
            case 'P365':
                return '365 platform'
            case 'PC':
                return 'Promotional campaign'
            case 'R':
                return 'Registration'
            case 'SP':
                return 'Sales Process'
            case 'VE':
                return 'Virtual event'
            case 'W':
                return 'Website'
            case 'WV':
                return 'Webinar / Virtual Event'
            default:
                return ''
        }
    }

    handlePresentationTitleChange = (e) => {
        if (!this.presentation) return

        this.presentation.title = e.value
    }

    showLanding = () => {
        this.event = null
        this.entrySurvey = null
        this.startingSurveyId = null
        this.stage = 'landing'
    }

    showForm = () => {
        this.stage = 'form'
    }

    showBuilding = () => {
        this.stage = 'building'
    }

    setEvent = (value) => {
        this.entrySurvey = null
        this.event = value
    }

    setEntrySurvey = (value) => {
        this.entrySurvey = value
    }

    startBuilding = async () => {
        this.buildingError = null
        this.showBuilding()
        try {
            await Promise.all([
                this.preloadBuilder(),
                new Promise((resolve) => setTimeout(resolve, BUILDING_MIN_DURATION_MS)),
            ])
            this.stage = 'builder'
        } catch (e) {
            console.error('Failed to open presentation', e)
            this.buildingError = e?.message || 'We couldn\'t open this presentation.'
        }
    }

    setBuildingError = (message) => {
        this.buildingError = message
    }

    clearBuildingError = () => {
        this.buildingError = null
    }

    deletePresentation = async () => {
        try {
            await this.appStore.http.delete(`/export/ppt/${this.presentationToDelete}`)
            this.presentations = this.presentations
                .filter((p) => p.uuid !== this.presentationToDelete && p.id !== this.presentationToDelete)
            this.filterPresentations()
            this.appStore.addNotification(null, i18n.t('powerpoint.presentationDeleted'), 'success')
            this.handleCloseDeletePresentationModal()
        } catch (e) {
            console.error('Failed to delete presentation', e)
            this.appStore.addNotification(null, i18n.t('powerpoint.presentationDeleteFailed'), 'error')
        }
    }

    save = async () => {
        const url = await this.appStore.http.post('/export/ppt/v2', JSON.stringify(this.presentation))
        const a = document.createElement('a')
        a.href = url
        a.download = 'ppt.pptx'
        document.body.appendChild(a)
        a.click()
        a.remove()
    }

    handleSavePresentation = async () => {
        this.saving = true
        try {
            console.log('Saving presentation', this.presentation)
            const resp = await this.appStore.http.post(`/export/ppt/${this.presentation.id}`, JSON.stringify({
                surveyId: this.presentation.surveyId[0],
                data: JSON.stringify(this.presentation),
            }))

            this.lastSavedAt = Date.now()
            this.nowTick = Date.now()
            console.log('Saved presentation', resp)
            this.appStore.addNotification(null, i18n.t('powerpoint.presentationSaved'), 'success')
        } catch (e) {
            console.error('Failed to save presentation', e)
            this.appStore.addNotification(null, i18n.t('powerpoint.presentationSaveFailed'), 'error')
        } finally {
            this.saving = false
        }
    }

    get saveStatus() {
        if (this.saving) return 'saving'
        if (this.saveTimer) return 'pending'
        return 'saved'
    }

    get saveStatusLabel() {
        if (this.saving) return 'Saving...'
        if (this.saveTimer) return 'Save pending'
        if (this.lastSavedAt) {
            return 'Up to date'
        }
        return 'Saved'
    }

    get eventTitle() {
        const evnt = this.products.find((e) => e.productId === this.survey.productId)
        if (!evnt) return ''
        return evnt.name
    }

    setEntryTitle = (e) => {
        this.entryTitle = e.value
    }

    get slidesMissingQuestions() {
        const missing = []
        this.presentation.section.forEach((section, sectionIndex) => {
            section.slide.forEach((slide, slideIndex) => {
                if (NO_QUESTION_SLIDE_TEMPLATES.includes(slide.template)) return
                if (slide.question.size === 0) {
                    missing.push({
                        sectionTitle: section.title,
                        sectionIndex,
                        slideIndex,
                    })
                }
            })
        })
        return missing
    }

    requestDownload = () => {
        if (this.slidesMissingQuestions.length > 0) {
            this.downloadWarningOpen = true
            return
        }
        this.startDownload()
    }

    closeDownloadWarning = () => {
        this.downloadWarningOpen = false
    }

    removeEmptySlidesAndDownload = () => {
        this.recordHistory()

        for (let s = this.presentation.section.length - 1; s >= 0; s -= 1) {
            const section = this.presentation.section[s]
            for (let sl = section.slide.length - 1; sl >= 0; sl -= 1) {
                const slide = section.slide[sl]
                if (!NO_QUESTION_SLIDE_TEMPLATES.includes(slide.template) && slide.question.size === 0) {
                    section.deleteSlide(sl)
                }
            }
            if (section.slide.length === 0) {
                this.presentation.removeSection(s)
            }
        }

        const sectionCount = this.presentation.section.length
        const safeSection = Math.min(this.currentSection, Math.max(0, sectionCount - 1))
        const slideCountForSection = this.presentation.section[safeSection]?.slide.length ?? 0
        const safeSlide = Math.min(this.currentSlide, Math.max(0, slideCountForSection - 1))
        this.changeCurrentSlide(safeSection, safeSlide)

        this.downloadWarningOpen = false
        this.scheduleSave()
        this.startDownload()
    }

    startDownload = async () => {
        if (this.downloadModalState === 'preparing') return

        this.trackPresentation(MIXPANEL_EVENTS.PRESENTATION_DOWNLOADED)

        if (this.downloadFile?.url) URL.revokeObjectURL(this.downloadFile.url)

        this.downloadModalState = 'preparing'
        this.downloadStepIndex = 0
        this.downloadFile = null
        this.downloadError = null

        this.downloadStepTimers = []
        for (let i = 1; i < DOWNLOAD_STEPS; i += 1) {
            const timer = setTimeout(() => {
                if (this.downloadModalState === 'preparing') this.downloadStepIndex = i
            }, DOWNLOAD_STEP_MS * i)
            this.downloadStepTimers.push(timer)
        }

        const slideCountAtStart = this.slideCount
        const minDuration = new Promise((resolve) => setTimeout(resolve, DOWNLOAD_MIN_DURATION_MS))

        try {
            const remoteUrl = await this.appStore.http.post(
                '/export/ppt/v2',
                JSON.stringify(this.presentation),
            )
            const response = await fetch(remoteUrl)
            if (!response.ok) throw new Error(`Download fetch failed (${response.status})`)
            const blob = await response.blob()

            await minDuration

            const presentationName = this.presentationTitle?.trim() || 'presentation'
            const safeName = presentationName.replace(/[\\/:*?"<>|]+/g, '')
            const filename = `${safeName} — report.pptx`
            const sizeMB = (blob.size / (1024 * 1024)).toFixed(1)

            this.downloadFile = {
                blob,
                url: URL.createObjectURL(blob),
                filename,
                size: sizeMB,
                slideCount: slideCountAtStart,
            }
            this.downloadModalState = 'ready'
        } catch (e) {
            console.error('Download generation failed', e)
            this.downloadError = e?.message || 'Failed to generate presentation'
            this.downloadModalState = 'error'
        } finally {
            this.downloadStepTimers.forEach(clearTimeout)
            this.downloadStepTimers = []
        }
    }

    triggerDownload = () => {
        if (!this.downloadFile) return
        const a = document.createElement('a')
        a.href = this.downloadFile.url
        a.download = this.downloadFile.filename
        document.body.appendChild(a)
        a.click()
        a.remove()
    }

    closeDownloadModal = () => {
        if (this.downloadFile?.url) URL.revokeObjectURL(this.downloadFile.url)
        this.downloadFile = null
        this.downloadError = null
        this.downloadModalState = 'idle'
        this.downloadStepIndex = 0
    }

    init = async () => {
        const [
            products,
            portfolios,
            divisions,
            globalYears,
            benchmarkSet,
        ] = await Promise.all([
            this.getProducts(),
            this.getPortfolios(),
            this.getDivisions(),
            this.getGlobalYears(),
            this.getBenchmarks(),
            this.getProduct(),
            this.getSurveys(),
        ])

        this.globalYears = globalYears
        const questionYears = await this.getQuestionYears()
        const latestSurveys = Object.values(this.surveys
            .filter((survey) => (
                survey.status !== 4
                    && [1, 2, 3, 6].includes(survey.productStatus)
                    && survey.isPrimary
                    && survey.surveyType !== 'GE'
            ))
            .reduce((map, survey) => {
                const { respondentType, surveyId, year } = survey
                map[respondentType] ||= {
                    year: 0,
                }

                if (year > map[respondentType].year) {
                    map[respondentType] = {
                        surveyId,
                        year,
                    }
                }

                return map
            }, {})).map(({ surveyId }) => surveyId)
        const benchmarkTags = await this.getBenchmarkTags(latestSurveys)
        this.questionYears.set(this.activeSurvey.id.toUpperCase(), questionYears)
        this.questionBenchmarks.set(this.activeSurvey.id.toUpperCase(), benchmarkSet)
        await this.handleSelectSurvey({ value: this.activeSurvey.id.toUpperCase() })
        if (!this.presentation.locale) this.presentation.locale = this.fullSurveys.get(this.activeSurvey?.id).survey.metadata.defaultLocale
        this.globalBenchmarks = this.getGlobalBenchmarks()
        this.productNames = products
        this.portfolioNames = portfolios
        this.divisionNames = divisions
        this.benchmarkTags = this.formatBenchmarkTags(benchmarkTags)
        this.tagBenchmarks = this.getTagBenchmarks(benchmarkTags)

        this.tagBenchmarks.forEach(({ value, label, shortLabel }) => {
            // this.benchmarkLabels[value] = label
            this.benchmarks.set(value, false)
            // this.tagBenchmarkLabels[value] = shortLabel
        })

        const tagNames = benchmarkTags.reduce((tn, bt) => {
            bt.tagOptions.forEach((top) => {
                const toc = process.env.NEW_CUSTOM_BMTAGS_ENDPOINT ? top.tagOptionConfig : JSON.parse(top.tagOptionConfig)
                tn = {
                    ...tn,
                    [top.tagOptionId]: R.pathOr(
                        R.pathOr('', ['langs', 'en_GB'], toc), ['name'], toc,
                    ),
                }
            })
            return tn
        }, {})
        this.tagNames = tagNames

        this.dataFormatter = new DataFormatter(
            this.appStore,
            this.productNames,
            this.portfolioNames,
            this.divisionNames,
            this,
            tagNames,
        )
    }

    get activeSection() {
        return this.presentation.section[this.currentSection]
    }

    get activeSlide() {
        return this.activeSection?.slide[this.currentSlide]
    }

    get activeChart() {
        return this.activeSlide?.question?.get(this.currentChart)
    }

    get sections() {
        return this.presentation.section
    }

    get derivedKpiQuestions() {
        return this.questions
            .filter((q) => ['RD', 'SB']
                .includes(q.question.questionTypeCode) && !!(q.question.optionSet.hasNumericCodes))
    }

    get derivedQuestions() {
        return this.questions
            .filter((q) => q.question.questionTypeCode === 'TB' && !!(q.question.optionSetX.hasNumericCodes))
    }

    get activeSlideTitle() {
        if (!this.activeSlide?.question) return ''

        return this.activeSlide.slideTitle
    }

    get activeSlideRespondentTypes() {
        if (!this.activeSlide?.question) return ''
        const slideQuestionRespTypes = Array.from(new Set(Array.from(this.activeSlide.question?.values()).map((q) => q.respondentType)))

        return slideQuestionRespTypes.map((t) => (t === 'EX' ? 'Exhibitor' : 'Attendee')).join('/')
    }

    get surveyOptions() {
        return this.surveys.map((s) => ({ value: s.surveyId, label: s.surveyName }))
    }

    get activeChartData() {
        if (this.activeChart?.kpiType === 'LMX') {
            return this.chartData.get(`${this.activeChart.surveyId}-LMX`)
        }
        if (this.activeChart?.kpiType === 'OSS') {
            return this.chartData.get(`${this.activeChart.surveyId}-OSS`)
        }
        return this.chartData.get(`${this.activeChart?.surveyId}-${this.activeChart?.questionId}`)
    }

    get sectionTitleForEdit() {
        if (this.editingSection === null) return ''
        return this.presentation.section[this.editingSection].title || ''
    }

    chartTypesForQuestion({ questionTypeCode, kpiType, hasNumericCodes }) {
        return this.chartTypeOptions.filter(({ filter }) => filter(questionTypeCode, kpiType, hasNumericCodes))
    }

    get questionYearsForChart() {
        if (this.activeChart?.kpiType === 'LMX') {
            return Object.keys(this.globalYears).map(Number).reverse()
        }
        if (this.activeChart?.kpiType === 'OSS') {
            return Object.keys(this.globalYears).map(Number).reverse()
        }
        return [...new Set(this.questionYears.get(this.activeChart?.surveyId)[this.activeChart?.questionId]?.map((qy) => qy.label))]
            .filter((y) => y !== this.activeChart?.year)
    }

    get presentationComparisonYears() {
        const comps = {}
        Object.keys(this.globalYears).forEach((gy) => comps[gy] =this.globalYears[gy].map((y) => y.checked).every((v) => !!v))
        return comps
    }

    get inheritedChildQuestions() {
        return this.questions
            .filter((q) => !!q.inheritedPageQuestionId
                && q.inheritedPageQuestionId !== '00000000-0000-0000-0000-000000000000')
    }

    get inheritedParentQuestion() {
        return this.questions.find((q) => q.question.id === this.inheritedChartModalSelections.parent)
    }

    get inheritedChildQuestion() {
        return this.questions.find((q) => q.question.id === this.inheritedChartModalSelections.child)
    }

    get inheritedChartTypeSelectable() {
        return this.inheritedChildQuestion?.question?.questionTypeCode === 'TB'
            && this.inheritedParentQuestion?.question?.questionTypeCode === 'CH'
    }

    get inheritedParentHasOtherOrExclusive() {
        const options = R.pathOr([], ['question', 'optionSet', 'options'], this.inheritedParentQuestion)
        return options.some(({ hasOptionOther, isExclusive }) => hasOptionOther || isExclusive)
    }

    get crossbreakCapableQuestions() {
        return this.questions.filter((q) => ['RD', 'TB', 'CH', 'SB'].includes(q.question.questionTypeCode))
    }

    getSurveyDefaultLocale = (surveyId) => R.pathOr(
        undefined,
        ['survey', 'metadata', 'defaultLocale'],
        this.fullSurveys.get(surveyId?.toUpperCase?.() ?? surveyId),
    )

    isTbStackedReporting = (chartData, chart) => (
        chartData?.questionTypeCode === 'TB'
        && ['stackedBar', 'stackedColumn'].includes(chart)
        && Object.keys(chartData?.optionReportingValues || {}).length > 0
    )

    getChartDataForQuestion(qid, pos) {
        const chartData = this.chartData.get(qid)

        if (!chartData) return []

        const ppQuestion = this.activeSlide?.question.get(pos)
        let baseData = chartData?.data.filter((d) => !d.benchmarkName)

        baseData = baseData.map((d) => ({
            ...d,
            show: ppQuestion.comparisonYears.includes(d.year) || ppQuestion.year === d.year,
        }))
        const qbench = this.questionBenchmarks?.get(ppQuestion.surveyId)?.[this.activePeriodKey]?.benchmarks?.[ppQuestion.questionId]

        if (!qbench || !ppQuestion?.benchmark?.length) {
            return {
                ...chartData,
                data: baseData,
                benchmarks: [],
            }
        }

        const tbStacked = this.isTbStackedReporting(chartData, ppQuestion?.chart)
        const chartbench = this.globalBenchmarks.filter((gb) => ppQuestion?.benchmark.includes(gb.value))
        const benchmarks = []
        chartbench.forEach((gb) => {
            if (['NPS', 'pct'].includes(chartData.kpiType) && qbench[gb.value]?.pct) {
                const benchmarkRow = {
                    data: qbench[gb.value].pct,
                    benchmarkName: capitalize(gb.value),
                    benchmarkPeriod: this.benchmarkPeriod,
                }

                if (tbStacked) benchmarks.push(benchmarkRow)
                else baseData.push(benchmarkRow)
            }
        })

        return {
            ...chartData,
            data: baseData,
            benchmarks,
        }
    }

    getSurveys = async () => {
        const { appStore } = this
        const survey = this.activeSurvey
        const res = await appStore.http.get('/survey')
        const matchingSurv = res.filter((s) => s.year <= survey.getYear() && s.surveyType === R.pathOr('', ['metadata', 'taxonomy', 'surveyType'], survey) && s.productId === R.pathOr(0, ['metadata', 'productInfo', 'productId'], survey))
        this.surveys = matchingSurv
    }

    newMapping = () => {
        const { survey } = this
        const reportPages = survey?.standardPages?.map((sp) => toReportPage(sp))
        this.reportPages = reportPages.reduce(({ pages, parentMap }, rp) => {
            rp.questions = rp.questions.map((question) => {
                const { pageQuestionId, parentId, optionSetY } = question
                const parent = parentId
                    ? parentMap.get(parentId.toUpperCase())
                    : null
                const q = mapToQuestion(pageQuestionId, this.reportStore, parent, optionSetY, question)
                if (q.length === 1) {
                    parentMap.set(pageQuestionId.toUpperCase(), q[0])
                }
                return q
            }).flat()
            pages.push(rp)
            return {
                pages,
                parentMap,
            }
        }, {
            pages: [],
            parentMap: new Map(),
        }).pages
    }

    get activePeriodKey() {
        return this.benchmarkPeriod === 'ROL-84' ? 'rol84' : 'rol36'
    }

    getGlobalBenchmarks = () => {
        const { appStore, oidMap } = this

        const questionOids = Array.from(this.questions.reduce((set, { question }) => (
            set.add(question.metadata.oid)
        ), new Set()))

        const benchmarks = Object.keys(oidMap)
            .filter((benchmark) => (
                R.pathOr(false, ['userData', 'benchmarks', benchmark], appStore)
                    && (
                        (['industry', 'sector'].includes(benchmark) && questionOids.some((oid) => oid === 1))
                        || (benchmark !== 'industry' && questionOids.some((oid) => oid <= oidMap[benchmark]))
                    )
            ))
            .map((benchmark) => ({
                name: ENUM().benchmarks,
                value: benchmark,
                checked: ['industry', 'company'].includes(benchmark),
                oid: benchmark === 'sector' ? 6 : oidMap[benchmark],
                label: capitalize(benchmark),
            }))

        return R.sortWith(
            [R.ascend(R.prop('oid'))],
            benchmarks,
        )
    }

    getBenchmarksForSurvey = (surveyId) => {
        const key = surveyId?.toUpperCase?.()
        const surveySet = this.questionBenchmarks?.get(key)?.[this.activePeriodKey]?.availableBenchmarks ?? {}
        return this.globalBenchmarks.filter((gb) => surveySet[gb.value])
    }

    getDetailsType = (data, nonQuestionType) => {
        const responseType = R.pathOr('', ['responseType'], data)
        if (R.isNil(nonQuestionType) || nonQuestionType !== 'KPI') return responseType

        const dataType = R.pathOr(0, ['data', 0, 'data'], data)

        return typeof dataType === 'object' ? 'pct' : 'avg'
    }

    handleChangeChartType = (e) => {
        const qid = `${this.activeChart?.surveyId}-${this.activeChart?.questionId}`
        const chartData = this.chartData.get(qid)
        this.chartData.set(qid, {
            ...chartData,
            widgetRenderSettings: {
                ...chartData.widgetRenderSettings,
                labelPosition: e.value === 'standardBar'
                    ? this.defaultLabelPositions.bar : this.defaultLabelPositions.default,
            },
        })

        const pieTypes = ['pie', 'doughnut']
        const wasPie = pieTypes.includes(this.activeChart?.chart)
        const isPie = pieTypes.includes(e.value)

        if (isPie && !wasPie) {
            this.activeChart.benchmark = []
            this.activeChart.comparison = []
            this.activeChart.comparisonYears = []
        } else if (!isPie && wasPie) {
            this.applyGlobalDefaultsToChart(this.activeChart)
        }
    }

    applyGlobalDefaultsToChart = (ppQuestion) => {
        if (!ppQuestion) return
        const surveyQuestion = this.questions.find((q) => (
            String(R.pathOr('', ['question', 'id'], q)).toUpperCase() === String(ppQuestion.questionId).toUpperCase()
        ))
        if (!surveyQuestion) return
        const defaults = this.mapQuestionToChartDefaults(surveyQuestion)
        ppQuestion.benchmark = defaults.benchmark
        ppQuestion.comparison = defaults.comparison
        ppQuestion.comparisonYears = defaults.comparisonYears
        const qid = `${ppQuestion.surveyId}-${ppQuestion.questionId}`
        if (!this.chartData.get(qid)) {
            this.getQuestionData([{ ...surveyQuestion, filter: ppQuestion.filter }])
        }
    }

    updateRemoveZeroNull = (e) => {
        if (e.name !== 'removeZero') return

        const ac = this.activeChart
        const qid = `${ac?.surveyId}-${ac?.questionId}`
        const chartData = this.chartData.get(qid)
        const newValue = e.value ? 'zero' : 'dontRemove'

        this.chartData.set(qid, {
            ...chartData,
            widgetRenderSettings: {
                ...chartData.widgetRenderSettings,
                removeZeroNull: newValue,
            },
        })
    }

    getCrossTabChartData = async (ppQuestion) => {
        const { appStore } = this

        const resp = await appStore.http.post('/export/ppt/crossbreak', JSON.stringify({
            locale: 'en_GB',
            benchmarkPeriod: { periodType: 'ROL', duration: 36 },
            question: ppQuestion,
        }))

        if (!resp) return

        const barData = []

        R.pathOr([], ['barData', 'labels'], resp).forEach((l, i) => {
            barData.push({
                name: l,
                value: R.pathOr(null, ['barData', 'values', i], resp),
            })
        })

        const lineData = []

        R.pathOr([], ['lineData'], resp).forEach((ld) => {
            const d = []
            R.pathOr([], ['labels'], ld).forEach((l, i) => (
                d.push({
                    name: l,
                    value: R.pathOr(null, ['values', i], ld),
                })
            ))
            lineData.push({
                data: d,
                name: ld.name,
            })
        })

        this.chartData.set(`${ppQuestion.surveyId}-${ppQuestion.questionId}-crossbreak`, {
            data: [{
                data: barData,
                name: ppQuestion.kpiQuestionShortText || String(ppQuestion.questionShortText || '').split(' vs ')[0],
                tagOptionId: undefined,
                year: this.survey.year,
            }],
            lineData,
            kpiType: 'crossbreak',
            nonQuestionType: 'crossbreak',
            details: { type: 'nps' },
            options: [],
            columns: [],
            isCurrency: false,
            benchmarkType: 'YOY',
            scope: this.reportScope,
            widgetRenderSettings: {
                showLegend: true,
                showDataLabels: true,
                labelPosition: this.defaultLabelPositions.default,
                showOptionLabels: true,
                rotateXAxisLabels: false,
                orientation: 'horizontal',
                swapSeriesXAxis: false,
                format: '',
                sortBy: 'none',
                sortDirection: -1,
                removeZeroNull: 'dontRemove',
                dataDecimals: 2,
                showTitle: false,
                showYAxis: true,
                scrollable: false,
            },
        })
    }

    getInheritedChartData = async (parent, child) => {
        const { appStore } = this
        const reqData = [
            {
                benchmarkType: 'YOY',
                groupId: '',
                industry: this.survey.industry,
                productId: this.survey.productId,
                productType: this.survey.producttypecode,
                respondentType: this.survey.respondentType,
                responseType: 'pct',
                ...this.reportScopeFields,
                surveyType: this.survey.surveyType,
                questionId: parent.question.id,
                locale: this.presentation.locale,
            },
            {
                benchmarkType: 'YOY',
                groupId: '',
                industry: this.survey.industry,
                productId: this.survey.productId,
                productType: this.survey.producttypecode,
                respondentType: this.survey.respondentType,
                responseType: 'avg',
                ...this.reportScopeFields,
                surveyType: this.survey.surveyType,
                questionId: child.question.id,
                parentId: parent.question.id,
                locale: this.presentation.locale,
            },
        ]

        const resp = await appStore.http.post('/report/v1/dashboard', JSON.stringify({
            widget: reqData,
        }))

        if (!resp) return

        const barData = []
        const lineData = []
        const averages = []

        const parentYearData = R.pathOr([], ['widget', 0, 'data'], resp).find((d) => d.year === this.survey.year)
        const childYearData = R.pathOr([], ['widget', 1, 'data'], resp).find((d) => d.year === this.survey.year)

        const childBaseFor = (optId) => {
            const upper = optId?.toUpperCase?.() ?? optId
            const base = R.pathOr(
                R.pathOr(
                    R.pathOr(undefined, ['responses', optId], childYearData),
                    ['responses', upper], childYearData,
                ),
                ['widget', 1, 'responses', upper], resp,
            )
            return base == null ? null : Number(base)
        }

        const options = []

        R.pathOr([], ['widget', 0, 'options'], resp).forEach(([optId, label]) => {
            barData.push({
                name: label,
                value: R.pathOr(null, ['data', optId], parentYearData),
                childBase: childBaseFor(optId),
            })
            lineData.push({
                name: label,
                value: R.pathOr(null, ['data', optId], childYearData),
            })
            options.push([optId, label])
        })

        R.pathOr([], ['widget', 1, 'data'], resp).forEach((data) => {
            averages.push({
                year: data.year,
                data: data.data,
            })
        })

        const otherOrExclusiveOptionIds = new Set(
            R.pathOr([], ['question', 'optionSet', 'options'], parent)
                .filter(({ hasOptionOther, isExclusive }) => hasOptionOther || isExclusive)
                .map(({ id }) => id)
        )
        const otherOptionIds = new Set(
            R.pathOr([], ['question', 'optionSet', 'options'], parent)
                .filter(({ hasOptionOther }) => hasOptionOther)
                .map(({ id }) => id)
        )
        const exclusiveOptionIds = new Set(
            R.pathOr([], ['question', 'optionSet', 'options'], parent)
                .filter(({ isExclusive }) => isExclusive)
                .map(({ id }) => id)
        )
        const otherOrExclusiveLabels = new Set(
            R.pathOr([], ['widget', 0, 'options'], resp)
                .filter(([id]) => otherOrExclusiveOptionIds.has(id))
                .map(([, label]) => label)
        )
        const otherLabels = new Set(
            R.pathOr([], ['widget', 0, 'options'], resp)
                .filter(([id]) => otherOptionIds.has(id))
                .map(([, label]) => label)
        )
        const exclusiveLabels = new Set(
            R.pathOr([], ['widget', 0, 'options'], resp)
                .filter(([id]) => exclusiveOptionIds.has(id))
                .map(([, label]) => label)
        )

        const optionReportingValues = [
            ...R.pathOr([], ['question', 'optionSetX', 'options'], parent),
            ...R.pathOr([], ['question', 'optionSet', 'options'], parent),
            ...R.pathOr([], ['question', 'optionSetX', 'options'], child),
            ...R.pathOr([], ['question', 'optionSet', 'options'], child),
        ].reduce((acc, o) => (
            o?.id != null && o.reportingValue != null
                ? { ...acc, [String(o.id).toLowerCase()]: o.reportingValue }
                : acc
        ), {})

        this.chartData.set(`${this.survey.surveyId}-${parent.question.id}-inherited`, {
            ...R.pathOr({}, ['widget', 0], resp),
            averages,
            data: [{
                benchmark: undefined,
                benchmarkName: undefined,
                data: barData,
                name: this.getTitle(parent.question),
                tagOptionId: undefined,
                tagOptionName: undefined,
                year: this.survey.year,
            }],
            lineData: [{
                data: lineData,
                name: this.getTitle(child.question),
            }],
            otherOrExclusiveLabels,
            otherLabels,
            optionReportingValues,
            exclusiveLabels,
            kpiType: 'inherited',
            nonQuestionType: 'inherited',
            details: { type: 'avg' },
            options,
            columns: [],
            isCurrency: false,
            benchmarkType: 'YOY',
            scope: this.reportScope,
            widgetRenderSettings: {
                showDataLabels: true,
                format: '%',
                dataDecimals: 0,
            },
        })
    }

    getDerivedChartData = async (ppQuestion) => {
        const { appStore } = this

        const resp = await appStore.http.post('/export/ppt/derived', JSON.stringify({ ...ppQuestion, store: undefined, ppStore: undefined }))

        if (!resp) return

        const data = []

        R.pathOr([], ['xValues', 'labels'], resp).forEach((l, i) => {
            data.push({
                x: R.pathOr(null, ['xValues', 'values', i], resp),
                y: R.pathOr(null, ['yValues', 'values', i], resp),
                z: R.pathOr(null, ['yValues', 'sizes', i], resp),
                name: l,
                series: l,
            })
        })

        this.chartData.set(`${ppQuestion.surveyId}-${ppQuestion.questionId}-${ppQuestion.kpiQuestionId}`, {
            data,
            kpiType: 'quadrantAnalysis',
            nonQuestionType: 'quadrantAnalysis',
            dataDetails: { type: 'avg' },
            options: [],
            columns: [],
            isCurrency: false,
            quadrantCurrency: { yAxis: false, xAxis: false },
            benchmarkType: 'YOY',
            scope: this.reportScope,
            widgetRenderSettings: {
                showLabels: false,
                showLegend: true,
                showTitle: false,
                yAxisTitle: `${ppQuestion.questionShortText} Average score`,
                xAxisTitle: `Strength of correlation with ${ppQuestion.kpiQuestionShortText}`,
            },
        })
    }

    fetchLMXChartData = async () => {
        const { appStore } = this
        const groupId = uuidv4()
        const reqData = [{
            benchmarkType: 'YOY',
            groupId,
            industry: this.survey.industry,
            productId: this.survey.productId,
            productType: this.survey.producttypecode,
            respondentType: this.survey.respondentType,
            responseType: 'LMX',
            ...this.reportScopeFields,
            surveyType: this.survey.surveyType,
            questionId: '00000000-0000-0000-0000-000000000000',
            locale: this.presentation.locale,
        }]

        const benchmarks = [
            {
                benchmarkType: 'ROL',
                groupId: 'industry_ROL-36',
                period: {
                    periodType: 'ROL',
                    duration: 36,
                },
                industry: this.survey.industry,
                productId: this.survey.productId,
                productType: this.survey.producttypecode,
                respondentType: this.survey.respondentType,
                responseType: 'LMX',
                scope: 'industry',
                surveyType: this.survey.surveyType,
                questionId: '00000000-0000-0000-0000-000000000000',
                locale: this.presentation.locale,
            }, {
                benchmarkType: 'ROL',
                groupId: 'industry_ROL-84',
                period: {
                    periodType: 'ROL',
                    duration: 84,
                },
                industry: this.survey.industry,
                productId: this.survey.productId,
                productType: this.survey.producttypecode,
                respondentType: this.survey.respondentType,
                responseType: 'LMX',
                scope: 'industry',
                surveyType: this.survey.surveyType,
                questionId: '00000000-0000-0000-0000-000000000000',
                locale: this.presentation.locale,
            }, {
                benchmarkType: 'ROL',
                groupId: 'client_ROL-36',
                period: {
                    periodType: 'ROL',
                    duration: 36,
                },
                industry: this.survey.industry,
                productId: this.survey.productId,
                productType: this.survey.producttypecode,
                respondentType: this.survey.respondentType,
                responseType: 'LMX',
                scope: 'client',
                surveyType: this.survey.surveyType,
                questionId: '00000000-0000-0000-0000-000000000000',
                locale: this.presentation.locale,
            }, {
                benchmarkType: 'ROL',
                groupId: 'client_ROL-84',
                period: {
                    periodType: 'ROL',
                    duration: 84,
                },
                industry: this.survey.industry,
                productId: this.survey.productId,
                productType: this.survey.producttypecode,
                respondentType: this.survey.respondentType,
                responseType: 'LMX',
                scope: 'client',
                surveyType: this.survey.surveyType,
                questionId: '00000000-0000-0000-0000-000000000000',
                locale: this.presentation.locale,
            }, {
                benchmarkType: 'ROL',
                groupId: 'portfolio_ROL-36',
                period: {
                    periodType: 'ROL',
                    duration: 36,
                },
                industry: this.survey.industry,
                productId: this.survey.productId,
                productType: this.survey.producttypecode,
                respondentType: this.survey.respondentType,
                responseType: 'LMX',
                scope: 'portfolio',
                portfolioId: this.products.find((e) => e.productId === this.survey.productId)?.portfolioId,
                surveyType: this.survey.surveyType,
                questionId: '00000000-0000-0000-0000-000000000000',
                locale: this.presentation.locale,
            }, {
                benchmarkType: 'ROL',
                groupId: 'portfolio_ROL-84',
                period: {
                    periodType: 'ROL',
                    duration: 84,
                },
                industry: this.survey.industry,
                productId: this.survey.productId,
                productType: this.survey.producttypecode,
                respondentType: this.survey.respondentType,
                responseType: 'LMX',
                scope: 'portfolio',
                portfolioId: this.products.find((e) => e.productId === this.survey.productId)?.portfolioId,
                surveyType: this.survey.surveyType,
                questionId: '00000000-0000-0000-0000-000000000000',
                locale: this.presentation.locale,
            }]

        const [resp, benchresp] = await Promise.all([
            appStore.http.post('/report/v1/dashboard', JSON.stringify({
                widget: reqData,
            })),
            appStore.http.post('/report/v1/dashboard', JSON.stringify({
                widget: benchmarks,
            })),
        ])

        if (!resp) return
        if (benchresp) {
            const benchData = {
                company: {},
                industry: {},
                sector: {},
                portfolio: {},
            }
            benchresp.widget.forEach((w) => {
                const [scope, duration] = w.groupId.split('_')
                if (scope === 'client') {
                    benchData.company[duration] = w.data[0]?.data
                } else {
                    benchData[scope][duration] = w.data[0]?.data
                }
            })
            this.lmxBenchmarkData.set(`${this.survey.surveyId}-LMX`, benchData)
        }
        const [qdata] = resp.widget.filter((w) => w.groupId === groupId)

        this.questionData.set(`${this.survey.surveyId}-LMX`, qdata)

        this.chartData.set(`${this.survey.surveyId}-LMX`, {
            ...qdata,
            dataDetails: { type: 'pct' },
            otherOrExclusiveLabels: [],
            columns: [],
            responses: [],
            min: 1,
            max: 100,
            averages: [],
            percentiles: undefined,
            isCurrency: false,
            quadrantCurrency: undefined,
            nonQuestionType: 'KPI',
            questionInitialLocale: '',
            questionTypeCode: '',
            questionOptions: [],
            kpiType: '',
            benchmarkType: '',
            baseSizes: [],
            surveyYear: this.survey.year,
            widgetRenderSettings: {
                showLegend: true,
                showDataLabels: true,
                labelPosition: this.defaultLabelPositions.default,
                showOptionLabels: false,
                rotateXAxisLabels: false,
                orientation: 'horizontal',
                swapSeriesXAxis: false,
                format: 'numeric',
                sortBy: 'none',
                sortDirection: -1,
                removeZeroNull: 'dontRemove',
                dataDecimals: 0,
                showTitle: false,
                showYAxis: false,
                scrollable: false,
            },
        })
    }

    fetchOSSChartData = async () => {
        const { appStore } = this
        const groupId = uuidv4()
        const reqData = [{
            benchmarkType: 'YOY',
            groupId,
            industry: this.survey.industry,
            productId: this.survey.productId,
            productType: this.survey.producttypecode,
            respondentType: this.survey.respondentType,
            responseType: 'OSS',
            ...this.reportScopeFields,
            surveyType: this.survey.surveyType,
            questionId: '00000000-0000-0000-0000-000000000000',
            locale: this.presentation.locale,
        }]

        const resp = await appStore.http.post('/report/v1/dashboard', JSON.stringify({
            widget: reqData,
        }))

        if (!resp) return
        const [qdata] = resp.widget.filter((w) => w.groupId === groupId)

        this.questionData.set(`${this.survey.surveyId}-OSS`, qdata)

        this.chartData.set(`${this.survey.surveyId}-OSS`, {
            ...qdata,
            dataDetails: { type: 'OSS' },
            options: [],
            otherOrExclusiveLabels: [],
            columns: [],
            responses: [],
            min: 1,
            max: 100,
            averages: [],
            percentiles: undefined,
            isCurrency: false,
            quadrantCurrency: undefined,
            nonQuestionType: 'KPI',
            questionInitialLocale: '',
            questionTypeCode: '',
            questionOptions: [],
            kpiType: '',
            benchmarkType: '',
            baseSizes: [],
            surveyYear: this.survey.year,
            widgetRenderSettings: {
                showLegend: true,
                showDataLabels: true,
                labelPosition: this.defaultLabelPositions.default,
                showOptionLabels: false,
                rotateXAxisLabels: false,
                orientation: 'horizontal',
                swapSeriesXAxis: false,
                format: 'numeric',
                sortBy: 'none',
                sortDirection: -1,
                removeZeroNull: 'dontRemove',
                dataDecimals: 0,
                showTitle: false,
                showYAxis: false,
                scrollable: false,
            },
        })
    }

    getResponseTypeForQuestion = (question) => {
        if (question.question.metadata.kpiType === 'NPS') {
            return this.showAverageByQuestionId.get(question.question.id) ? 'nps' : 'pct'
        }
        if (['RD', 'CH', 'SB', 'TB'].includes(question.question.questionTypeCode)) {
            return this.showAverageByQuestionId.get(question.question.id) ? 'avg' : 'pct'
        }
        return 'avg'
    }

    getParentOptions = (parentId) => {
        if (!parentId) return []

        const parentQuestion = this.questions.find((qq) => qq.pageQuestionId === parentId.toUpperCase())

        if (!parentQuestion) return []

        return this.getParentOptions(parentQuestion.inheritedPageQuestionId).concat(parentQuestion.question.optionSet.options.filter(({ isNonReporting }) => !isNonReporting))
    }

    getQuestionData = async (questions) => {
        const { appStore } = this
        const reqData = []
        const groupIds = []

        questions.forEach((q) => {
            const groupId = uuidv4()
            const { optionSetX, optionSet } = q.question
            const options = (optionSetX ?? optionSet)?.options ?? []
            const parentQuestion = this.questions.find((qq) => qq.pageQuestionId === q.inheritedPageQuestionId?.toUpperCase())
            const parentId = parentQuestion?.question?.id

            groupIds.push(groupId)

            reqData.push({
                benchmarkType: 'YOY',
                groupId,
                industry: this.survey.industry,
                productId: this.survey.productId,
                productType: this.survey.producttypecode,
                respondentType: this.survey.respondentType,
                responseType: this.getResponseTypeForQuestion(q),
                ...this.reportScopeFields,
                surveyType: this.survey.surveyType,
                questionId: q.question.id,
                parentId,
                filter: q.filter,
                locale: this.presentation.locale,
            })
            if (options.some(({ reportingValue }) => !!reportingValue)) {
                reqData.push({
                    benchmarkType: 'YOY',
                    groupId,
                    industry: this.survey.industry,
                    productId: this.survey.productId,
                    productType: this.survey.producttypecode,
                    respondentType: this.survey.respondentType,
                    responseType: q.question.metadata.kpiType === 'NPS' ? 'nps' : 'avg',
                    ...this.reportScopeFields,
                    surveyType: this.survey.surveyType,
                    questionId: q.question.id,
                    parentId,
                    filter: q.filter,
                    locale: this.presentation.locale,
                })
            }
        })

        const resp = await appStore.http.post('/report/v1/dashboard', JSON.stringify({
            widget: reqData,
        }))

        if (!resp) return

        questions.forEach((q, i) => {
            const groupId = groupIds[i]
            const { question, inheritedPageQuestionId } = q
            const { questionTypeCode, id } = question
            const [qdata, averageData] = resp.widget.filter((w) => w.groupId === groupId)
            const parentOptions = this.getParentOptions(inheritedPageQuestionId)
            this.questionData.set(`${this.survey.surveyId}-${id}`, qdata)
            let averages

            const baseSizes = qdata.data.reduce((map, { year, baseSize }) => ({
                ...map,
                [year]: baseSize,
            }), {})

            if (questionTypeCode === 'TB') {
                const data = averageData
                    ? averageData.data
                    : qdata.data.map(({ year, data }) => ({
                        year,
                        data: Object.entries(data).reduce((map, [k, v]) => {
                            const [key] = k.split('_')
                            map[key] ??= 0
                            map[key] += v
                            return map
                        }, {}),
                    }))
                const rows = parentOptions.concat(question.optionSetY.options)

                averages = data.reduce((map, { year, data }) => ({
                    ...map,
                    [year]: rows.reduce((rowMap, { id: rowId }) => ({ ...rowMap, [rowId]: data[rowId] }), {}),
                }), {})
            } else if (['RD', 'SB'].includes(questionTypeCode) && averageData) {
                const fData = averageData.data
                    .slice()
                    .sort((a, b) => Number(a.year) - Number(b.year))
                    .map(({ year, data }) => ({
                        name: year,
                        data: [{ value: data, name: year }],
                    }))
                const xAxisLabels = ['Average']
                averages = {
                    fData,
                    xAxisLabels,
                }
            }

            const pctFormat = this.getDetailsType(qdata) === 'pct' ? '%' : 'numeric'
            const otherOrExclusiveOptionIds = new Set(
                (question.optionSet?.options ?? [])
                    .filter(({ hasOptionOther, isExclusive }) => hasOptionOther || isExclusive)
                    .map(({ id }) => id)
            )
            const otherOptionIds = new Set(
                (question.optionSet?.options ?? [])
                    .filter(({ hasOptionOther }) => hasOptionOther)
                    .map(({ id }) => id)
            )
            const exclusiveOptionIds = new Set(
                (question.optionSet?.options ?? [])
                    .filter(({ isExclusive }) => isExclusive)
                    .map(({ id }) => id)
            )

            const surveyDefaultLocale = this.getSurveyDefaultLocale(this.survey.surveyId)
            const optionLangs = [
                ...parentOptions,
                ...R.pathOr([], ['optionSetX', 'options'], question),
                ...R.pathOr([], ['optionSet', 'options'], question),
                ...R.pathOr([], ['optionSetY', 'options'], question),
            ].reduce((acc, o) => (o?.id != null ? { ...acc, [o.id]: o.langs } : acc), {})
            const localiseOption = (optId, fallback) => {
                const langs = optionLangs[optId]
                if (!langs) return fallback
                return R.pathOr(fallback, ['text'], getOptionTitle({
                    langs,
                    locale: surveyDefaultLocale,
                    defaultLocale: question.metadata.initialLocale,
                    initialLocale: 'en_GB',
                }))
            }

            const otherOrExclusiveLabels = new Set(
                (qdata.options ?? [])
                    .filter(([optId]) => otherOrExclusiveOptionIds.has(optId))
                    .map(([optId, label]) => stripOutHtml(localiseOption(optId, label))),
            )
            const otherLabels = new Set(
                (qdata.options ?? [])
                    .filter(([optId]) => otherOptionIds.has(optId))
                    .map(([optId, label]) => stripOutHtml(localiseOption(optId, label))),
            )
            const exclusiveLabels = new Set(
                (qdata.options ?? [])
                    .filter(([optId]) => exclusiveOptionIds.has(optId))
                    .map(([optId, label]) => stripOutHtml(localiseOption(optId, label))),
            )

            const optionReportingValues = [
                ...parentOptions,
                ...R.pathOr([], ['optionSetX', 'options'], question),
                ...R.pathOr([], ['optionSet', 'options'], question),
            ].reduce((acc, o) => (
                o?.id != null && o.reportingValue != null
                    ? { ...acc, [String(o.id).toLowerCase()]: o.reportingValue }
                    : acc
            ), {})

            const d = {
                ...this.questionData.get(`${this.survey.surveyId}-${id}`),
                dataDetails: {
                    type: this.getDetailsType(qdata),
                },
                options: (qdata.options?.filter((o) => o !== null) ?? [])
                    .map(([optId, label]) => [optId, localiseOption(optId, label)]),
                optionReportingValues,
                surveyDefaultLocale,
                otherOrExclusiveLabels,
                otherLabels,
                exclusiveLabels,
                columns: qdata.columns?.filter((o) => o !== null),
                responses: qdata.responses ?? {},
                min: qdata.min || undefined,
                max: qdata.max || undefined,
                averages,
                percentiles: qdata.percentiles || undefined,
                isCurrency: qdata.isCurrency,
                quadrantCurrency: undefined,
                nonQuestionType: q.question.questionTypeCode === 'TB' ? '' : 'KPI',
                questionInitialLocale: q.question.metadata.initialLocale,
                questionTypeCode: q.question.questionTypeCode,
                questionOptions: parentOptions.concat(R.pathOr([], ['optionSetY', 'options'], q.question)),
                questionId: q.question.id,
                kpiType: this.getDetailsType(qdata),
                benchmarkType: '',
                baseSizes,
                widgetRenderSettings: {
                    showLegend: true,
                    showDataLabels: true,
                    labelPosition: this.defaultLabelPositions.default,
                    showOptionLabels: true,
                    orientation: 'horizontal',
                    swapSeriesXAxis: false,
                    format: qdata.isCurrency ? 'currency' : pctFormat,
                    sortBy: q.question.questionTypeCode === 'CH' ? 'value' : 'none',
                    sortDirection: -1,
                    removeZeroNull: 'dontRemove',
                    dataDecimals: this.getDetailsType(qdata) === 'pct' ? 0 : 2,
                    showTitle: false,
                    showYAxis: true,
                    scrollable: false,
                },
            }

            this.chartData.set(`${this.survey.surveyId}-${q.question.id}`, {
                ...d,
                fetchedWithFilter: toJS(q.filter),
            })
        })
    }

    getGlobalYears = async () => {
        const { appStore, surveyYear } = this
        const { client: { prodductMap } } = appStore
        const survey = this.activeSurvey
        const { productId, industrySpecificInfo: { productType, industry } } = survey.metadata.productInfo
        const { surveyType } = survey.metadata.taxonomy

        const allSurveyRespTypes = Object
            .keys(R.pathOr({}, [industry, 'children', productType, 'children', surveyType, 'children'], prodductMap))
        const arr = await Promise.all(allSurveyRespTypes
            .map(async (r) => appStore.http.get(`/product/${productId}/surveyyears/${surveyType}/${r}`)))

        const allSurveyYears = arr.flat()

        if (isEmpty(allSurveyYears)) {
            throw new Error('No results')
        }

        const mappedYears = allSurveyYears
            .filter(({ year }) => (
                Number(year) !== surveyYear
                && !Number.isNaN(Number(year))
            ))
            .reduce((map, { year, surveyId }) => (
                !Object.hasOwnProperty.call(map, year)
                    ? Object.assign(map, {
                        [year]: [
                            {
                                name: ENUM().comparisonSurveyIds,
                                value: surveyId.toUpperCase(),
                                label: String(year),
                                year: Number(year),
                                checked: (survey.getYear() - Number(year)) <= 2,
                            },
                        ],
                    })
                    : Object.assign(map, {
                        [year]: [
                            ...map[year],
                            {
                                name: ENUM().comparisonSurveyIds,
                                value: surveyId.toUpperCase(),
                                label: String(year),
                                year: Number(year),
                                checked: (survey.getYear() - Number(year)) <= 2,
                            },
                        ],
                    })
            ), {})

        return Object.fromEntries(
            Object.entries(mappedYears).sort(([a, _], [valB, __]) => Number(valB) - Number(a)),
        )
    }

    formatBenchmarkTags = (benchmarkTags) => benchmarkTags.map((tag) => {
        const { langcode } = this.appStore
        const newTagOptions = tag.tagOptions.map((top) => {
            const toc = process.env.NEW_CUSTOM_BMTAGS_ENDPOINT ? top.tagOptionConfig : JSON.parse(top.tagOptionConfig)
            const tagName = R.pathOr(
                R.pathOr(
                    R.pathOr('', ['langs', 'en_GB'], toc),
                    ['langs', langcode],
                    toc,
                ),
                ['name'], toc,
            )
            return {
                ...top,
                tagOptionName: tagName,
            }
        })
        return { ...tag, tagOptions: newTagOptions }
    })

    getBenchmarkTags = async () => {
        const url = process?.env?.NEW_CUSTOM_BMTAGS_ENDPOINT ? '/customBenchmarkTags' : '/benchmarkTags'
        if (process?.env?.USE_CUSTOM_BENCHMARKS !== 'true') return []
        const response = await this.appStore.http.get(url)
        return response.benchmarkTags
    }

    getProducts = async () => {
        const { appStore } = this
        try {
            const products = await appStore.http.get('/product')
            this.products = products
            return products.reduce((obj, p) => {
                if (this.oId === 6 && p.productId === this.recordId) {
                    this.productType = p.productType
                }
                return { ...obj, [p.productId]: `${p.name}${p.edition ? ` - ${p.edition}` : ''}` }
            }, {})
        } catch (e) {
            return {}
        }
    }

    getPortfolios = async () => {
        const { appStore } = this
        const { userData } = appStore
        const { role } = userData
        try {
            const portfolios = role > 130 && role !== 150 ? [] : await appStore.http.get('/portfolio')
            this.portfolios = portfolios
            return portfolios.reduce((obj, p) => ({ ...obj, [p.portfolioId]: p.name }), {})
        } catch (e) {
            this.portfolios = []
            return {}
        }
    }

    getDivisions = async () => {
        const { appStore } = this
        const { userData } = appStore
        const { role } = userData
        try {
            const divisions = role > 120 && role !== 150 ? [] : await appStore.http.get('/division')
            this.divisions = divisions
            return divisions.reduce((obj, p) => ({ ...obj, [p.divisionId]: p.name }), {})
        } catch (e) {
            this.divisions = []
            return {}
        }
    }

    getBenchmarks = async (surveyId) => {
        const { appStore } = this

        let benchmarks = {
            rol36: { availableBenchmarks: {} },
            rol84: { availableBenchmarks: {} },
        }

        try {
            const sid = surveyId ?? this.activeSurvey.id
            const [rol36, rol84] = await Promise.all([
                appStore.http.get(`/v2/survey/${sid}/report/benchmark?period=ROL&duration=36`),
                appStore.http.get(`/v2/survey/${sid}/report/benchmark?period=ROL&duration=84`),
            ])

            benchmarks = {
                rol36,
                rol84,
            }
        } catch (e) {
            console.error(e)
            appStore.addNotification(null, 'Error loading benchmarks', 'error')
        }

        return benchmarks
    }

    getProduct = async () => {
        const { appStore } = this

        try {
            const products = await appStore.http.get('/product')

            const presProd = products.find((product) => product.productId === this.activeSurvey?.metadata?.productInfo?.productId)

            if (presProd) {
                this.product = {
                    name: presProd.name,
                    edition: presProd.edition,
                }
            }
        } catch (e) {
            console.error(e)
        }
    }

    getQuestionYears = async (sid) => {
        const { appStore } = this
        const survey = this.activeSurvey
        const questionYears = await appStore.http.get(`/survey/${sid ?? survey.id}/yearsbenchmarks`)
        const globalYearStrings = Object.keys(this.globalYears).map((gy) => Number(gy))
        const globalChecked = {}
        Object.keys(this.globalYears).forEach((gy) => globalChecked[gy] = this.globalYears[gy].map((y) => y.checked).every((v) => !!v))
        return questionYears
            .reduce((map, { questionId, surveyid_yr }) => {
                const years = surveyid_yr.split(',').map((yearInfo) => {
                    const [surveyId, year] = yearInfo.split('_')
                    return {
                        label: Number(year),
                        value: surveyId,
                        checked: globalChecked[year],
                    }
                }).filter(({ label }) => globalYearStrings.includes(label))

                return Object.assign(map, {
                    [questionId]: R.sortWith(
                        [R.descend(R.prop('label'))],
                        years,
                    ),
                })
            }, {})
    }

    getQuestionBenchmarks = (questionId, surveyId) => {
        if (questionId === '00000000-0000-0000-0000-000000000000') {
            return this.globalBenchmarks.map((b) => cloneDeep(b))
        }

        const returnedBenchmarks = new Set(
            Object.keys(R.pathOr({}, [this.activePeriodKey, 'benchmarks', questionId], this.questionBenchmarks?.get(surveyId))),
        )
        return this.globalBenchmarks
            .filter(({ value }) => returnedBenchmarks.has(value))
            .map((b) => cloneDeep(b))
    }

    handleDerivedSelection = (qid, axis) => {
        this.derivedChartModalSelections[axis] = qid
    }

    handleToggleComparison = (y) => {
        const questionYearsComp = ['LMX', 'OSS'].includes(this.activeChart?.kpiType)
            ? this.globalYears[String(y)] || []
            : this.questionYears.get(this.activeChart?.surveyId)[this.activeChart?.questionId].filter((qy) => qy.label === y)
        if (this.activeChart.comparisonYears.includes(y)) {
            questionYearsComp.forEach((q) => this.activeChart.removeComparison(q.value, y))
        } else {
            questionYearsComp.forEach((q) => this.activeChart.addComparison(q.value, y))
        }
    }

    handleToggleBenchmark = (benchmark) => {
        if (this.activeChart.benchmark.includes(benchmark)) {
            this.activeChart.removeBenchmark(benchmark)
        } else {
            this.activeChart.addBenchmark(benchmark)
        }
    }

    handleCrossbreakToggleBenchmark = (benchmark) => {
        if (this.crossbreakChartModalSelections.benchmarks.includes(benchmark)) {
            this.crossbreakChartModalSelections.benchmarks = this.crossbreakChartModalSelections.benchmarks.filter((b) => b !== benchmark)
        } else {
            this.crossbreakChartModalSelections.benchmarks.push(benchmark)
        }
    }

    get benchmarkDataForChart() {
        const dataKey = this.activeChart?.kpiType === 'LMX'
            ? `${this.activeChart.surveyId}-LMX`
            : `${this.activeChart?.surveyId}-${this.activeChart?.questionId}`
        const chartData = this.chartData.get(dataKey)
        if (!chartData) return []

        let baseData = chartData?.data.filter((d) => !d.benchmarkName)

        baseData = baseData.filter((d) => this.activeChart?.comparisonYears.includes(d.year)
            || d.year >= this.activeChart?.year)

        const surveySet = this.questionBenchmarks?.get(this.activeChart.surveyId)
        const periodBenchmarks = surveySet?.[this.activePeriodKey]?.benchmarks
        const qbench = periodBenchmarks?.[this.activeChart?.questionId]

        if (!qbench || !this.activeChart?.benchmark?.length) return baseData

        if (this.isTbStackedReporting(chartData, this.activeChart?.chart)) return baseData

        const chartbench = this.globalBenchmarks.filter((gb) => this.activeChart?.benchmark.includes(gb.value))

        chartbench.forEach((gb) => {
            if (['NPS', 'pct'].includes(chartData.kpiType) && qbench[gb.value]?.pct) {
                baseData.push({
                    data: qbench[gb.value].pct,
                    benchmarkName: capitalize(gb.value),
                    benchmarkPeriod: this.benchmarkPeriod,
                })
            }
        })

        return baseData
    }

    handleToggleGlobalYear = (e) => {
        Object.keys(this.globalYears).slice().forEach((year) => {
            if (year === e.name) {
                this.globalYears[year].forEach((d) => d.checked = e.value)
            }
        })

        this.presentation.section.forEach((section) => {
            section.slide.forEach((slide) => {
                Array.from(slide.question.values()).forEach((question) => {
                    if (this.questionYears.get(question.surveyId)[question.questionId]) {
                        const questionYearsComp = this.questionYears.get(question.surveyId)[question.questionId]
                            .filter((qy) => qy.label === Number(e.name))
                        return e.value
                            ? questionYearsComp
                                .forEach((q) => question.addComparison(q.value, Number(e.name)))
                            : questionYearsComp
                                .forEach((q) => question.removeComparison(q.value, Number(e.name)))
                    }
                })
            })
        })
    }

    updateGlobalBenchmark = (e) => {
        this.benchmarkPeriod = e.value
        this.presentation.benchmarkPeriod = this.benchmarkPeriods.find(({ value }) => value === this.benchmarkPeriod)?.period
        this.refreshChartDataForActivePeriod()
    }

    refreshChartDataForActivePeriod = () => {
        const entries = Array.from(this.chartData.entries())
        entries.forEach(([key, value]) => {
            this.chartData.set(key, { ...value })
        })
    }

    handleToggleGlobalBenchmark = (e) => {
        this.globalBenchmarks = this.globalBenchmarks.slice().map((benchmark) => {
            if (benchmark.value === e.name) {
                benchmark.checked = e.value
            }
            return benchmark
        })

        this.presentation.toggleBenchmark(e.name)

        this.presentation.section.forEach((section) => {
            section.slide.forEach((slide) => {
                Array.from(slide.question.values()).forEach((question) => {
                    if (this.getQuestionBenchmarks(question.questionId, question.surveyId).length) {
                        this.getQuestionBenchmarks(question.questionId, question.surveyId).map((b) => {
                            if (b.checked) {
                                question.addBenchmark(b.value)
                            } else {
                                question.removeBenchmark(b.value)
                            }
                        })
                    }
                })
            })
        })
    }

    handleAddAllQuestions = async () => {
        this.recordHistory()
        this.trackPresentation(MIXPANEL_EVENTS.PRESENTATION_ADD_ALL_QUESTIONS, {
            question_count: this.questions.length,
        })
        this.questions.forEach((question) => {
            const {
                chart, sorting, benchmark, comparison, comparisonYears, hasNonNumericValues, hasNumericCodes, removeZero, locked, hasOtherOrExclusive, excludeNonNumeric, kpiType,
            } = this.mapQuestionToChartDefaults(question)
            this.activeSection.addSlide({
                template: chart === 'verbatim' ? chart : '1x1',
                title: '',
            }, this.currentSlide, 'below')
            this.changeCurrentSlide(this.currentSection, this.currentSlide + 1)
            this.activeSection.slide[this.currentSlide].addQuestion(0, {
                questionId: question.question.id,
                pageQuestionId: question.pageQuestionId.toLowerCase(),
                surveyId: this.survey.surveyId,
                questionShortText: this.getTitle(question.question),
                questionLongText: this.getQuestionLongText(question.question),
                respondentType: this.survey.respondentType,
                chart: chart ?? 'standardColumn',
                benchmark,
                comparison,
                comparisonYears,
                hasNonNumericValues,
                hasOtherOrExclusive,
                hasNumericCodes,
                year: this.survey.year,
                settings: { ...settingsDefaults, sorting, removeZero, locked, excludeNonNumeric },
                position: 0,
                ppStore: this,
                store: this.appStore,
                questionTypeCode: question.question.questionTypeCode,
                kpiType: kpiType ?? question.question.metadata.kpiType,
            })
        })
        await this.getQuestionData(this.questions)
        this.populatePresentationSurveyIds()
        this.changeCurrentSlide(this.currentSection + 1, this.currentSlide + 1)
        this.changeCurrentSlide(0, 0)
    }

    handleSelectInheritedChild = (e) => {
        const child = this.questions.find((q) => q.question.id === e.value)
        const parent = this.questions.find((q) => q.pageQuestionId === child.inheritedPageQuestionId.toUpperCase())

        const supportsStackedBar = child?.question?.questionTypeCode === 'TB'
            && parent?.question?.questionTypeCode === 'CH'

        // Seed comparison years/values from the child's defaults so a freshly
        // selected child starts with sensible comparisons the user can then edit.
        const { comparison, comparisonYears } = this.mapQuestionToChartDefaults(child)

        this.inheritedChartModalSelections = {
            ...this.inheritedChartModalSelections,
            parent: parent.question.id,
            child: child.question.id,
            chartType: supportsStackedBar
                ? this.inheritedChartModalSelections.chartType
                : 'standardColumn',
            comparison,
            comparisonYears,
        }
    }

    // Available comparison years for the child question selected in the inherited modal.
    get inheritedComparisonYearOptions() {
        const childId = this.inheritedChartModalSelections.child
        if (!childId) return []
        const childYears = this.questionYears.get(this.survey?.surveyId)?.[childId] ?? []
        return [...new Set(childYears.map((qy) => qy.label))]
            .filter((year) => year !== this.survey?.year)
            .sort((a, b) => a - b)
    }

    // Toggle a comparison year (and its associated comparison survey ids) in the
    // inherited modal selections.
    handleToggleInheritedComparison = (year) => {
        const sel = this.inheritedChartModalSelections
        const yearSurveyIds = (this.questionYears.get(this.survey?.surveyId)?.[sel.child] ?? [])
            .filter((qy) => qy.label === year)
            .map((qy) => qy.value)
        if (sel.comparisonYears.includes(year)) {
            sel.comparisonYears = sel.comparisonYears.filter((y) => y !== year)
            sel.comparison = sel.comparison.filter((c) => !yearSurveyIds.includes(c))
        } else {
            sel.comparisonYears = [...sel.comparisonYears, year]
            sel.comparison = [...new Set([...sel.comparison, ...yearSurveyIds])]
        }
    }

    handleOpenGlobalSettingsModal = () => {
        this.globalSettingsModalOpen = true
    }

    handleCloseGlobalSettingsModal = () => {
        this.globalSettingsModalOpen = false
    }

    handleOpenSlideSettingsModal = () => {
        this.slideSettingsModalOpen = true
    }

    handleCloseSlideSettingsModal = () => {
        this.slideSettingsModalOpen = false
    }

    handleCloseSlideSelectModal = () => {
        this.slideSelectModalOpen = false
        this.addPosition = null
    }

    handleOpenChartSettingsModal = () => {
        this.chartSettingsBaseline = this.takeSnapshot()
        this.chartSettingsModalOpen = true
    }

    commitChartSettingsHistory = () => {
        const baseline = this.chartSettingsBaseline
        this.chartSettingsBaseline = null
        if (!baseline || this.applyingHistory) return
        if (JSON.stringify(baseline) === JSON.stringify(this.takeSnapshot())) return
        this.recordHistory(baseline)
    }

    handleCloseChartSettingsModal = () => {
        this.commitChartSettingsHistory()
        const chart = this.activeChart
        const needsReflow = !!chart
            && ['stackedBar', 'stackedColumn', 'kpiChart', 'npsChart'].includes(chart.chart)
            && ((chart.benchmark?.length ?? 0) > 0 || (chart.comparisonYears?.length ?? 0) > 0)
        this.currentChart = null
        this.chartSettingsModalOpen = false
        if (needsReflow) this.reflowActiveSlideChart()
    }

    handleOpenLMXSettingsModal = () => {
        this.currentChart = 0
        this.chartSettingsBaseline = this.takeSnapshot()
        this.lmxSettingsModalVisible = true
    }

    handleCloseLMXSettingsModal = () => {
        this.commitChartSettingsHistory()
        this.currentChart = null
        this.lmxSettingsModalVisible = false
        this.fetchLMXChartData()
    }

    reflowActiveSlideChart = () => {
        const section = this.currentSection
        const slide = this.currentSlide
        const [awaySection, awaySlide] = section === 0 && slide === 0
            ? [0, (this.presentation.section[0]?.slide.length ?? 0) > 1 ? 1 : 0]
            : [0, 0]
        if (awaySection === section && awaySlide === slide) return
        this.changeCurrentSlide(awaySection, awaySlide)
        setTimeout(() => this.changeCurrentSlide(section, slide), 0)
    }

    handleOpenSlideSelectModal = (pos) => {
        if (pos === 'above' && !this.currentSection && !this.currentSlide) return
        this.slideSelectModalOpen = true
        this.addPosition = pos
    }

    handleOpenSectionTitleModal = (i) => {
        this.editingSection = i
        this.sectionTitleModalVisible = true
    }

    handleCloseSectionTitleModal = () => {
        this.sectionTitleModalVisible = false
        this.editingSection = null
    }

    handleOpenCrossbreakSettingsModal = () => {
        this.currentChart = 0
        const benchmarks = new Set()
        const activeGlobals = this.globalBenchmarks
            .filter((b) => b.checked)
            .map((b) => b.value)
        activeGlobals
            .filter((f) => this.crossbreakChartModalSelections.benchmarks
                .includes(f)).forEach((f) => benchmarks.add(f))
        this.crossbreakChartModalSelections.benchmarks.forEach((b) => benchmarks.add(b))
        this.crossbreakChartModalSelections.benchmarks = Array.from(benchmarks)
        this.crossbreakSettingsModalVisible = true
    }

    handleCloseCrossbreakSettingsModal = () => {
        this.crossbreakSettingsModalVisible = false
        this.crossbreakChartModalSelections = {
            questions: [
                {
                    question: '',
                    ranking: 'noRanking',
                    base: 0,
                },
            ],
            kpi: '',
            benchmarks: [],
        }
        this.editing = false
        this.currentChart = null
    }

    handleOpenFilterModal = (position) => {
        this.currentChart = position
        this.filterModalOpen = true
    }

    handleCloseFilterModal = async () => {
        this.currentChart = null
        this.filterModalOpen = false
    }

    handleSaveFilters = async (filterKey, ruleGroup) => {
        if (!this.activeChart) return
        if (!this.activeChart.filter) this.activeChart.filter = { ...filterDefaults }
        if (!ruleGroup.rules.length) {
            this.activeChart.filter = null
        } else {
            this.activeChart.filter[filterKey] = ruleGroup
        }

        this.trackPresentation(MIXPANEL_EVENTS.PRESENTATION_CHART_FILTER_APPLIED, {
            filter_key: filterKey,
            rule_count: ruleGroup?.rules?.length ?? 0,
            cleared: !ruleGroup.rules.length,
            chart_type: this.activeChart?.chart,
            question_id: this.activeChart?.questionId,
            slide_template: this.activeSlide?.template,
        })

        const q = this.questions.find(
            (qu) => String(qu.question.id) === String(this.activeChart?.questionId),
        )

        await this.getQuestionData([{ ...q, filter: this.activeChart?.filter }])
        this.recordHistory()
    }

    setOpenFilterPopover = (position) => {
        this.openFilterPopoverPosition = position
    }

    closeFilterPopover = () => {
        this.openFilterPopoverPosition = null
    }

    handleOpenInheritedSettingsModal = () => {
        this.inheritedSettingsModalVisible = true
    }

    handleCloseInheritedSettingsModal = () => {
        this.inheritedSettingsModalVisible = false
        this.inheritedChartModalSelections = {
            parent: '',
            child: '',
            ranking: 'highToLow',
            base: 0,
            average: 'average',
            removeZero: true,
            locked: true,
            chartType: 'standardColumn',
            comparison: [],
            comparisonYears: [],
        }
        this.editing = false
    }

    handleCloseDeletePresentationModal = () => {
        this.deletePresentationModalVisible = false
        this.presentationToDelete = null
    }

    handleOpenDeletePresentationModal = (uuid) => {
        this.presentationToDelete = uuid
        this.deletePresentationModalVisible = true
    }

    handleOpenVerbatimSettingsModal = (position, edit = false, slots = 3, chartSlot = 0) => {
        this.currentChart = chartSlot
        this.verbatimPosition = position
        this.verbatimSlots = slots

        if (edit) {
            const pptQuestion = this.activeSection.slide[this.currentSlide].getQuestionAtPosition(chartSlot)
            const category = pptQuestion.chosenCategories.get(position)
            pptQuestion.pendingChanges = {
                category: category?.category ?? '',
                labels: category?.labels ?? [],
            }
        }

        this.verbatimSettingsModalVisible = true
    }

    handleCloseVerbatimSettingsModal = () => {
        this.currentChart = null
        this.verbatimSettingsModalVisible = false
        this.verbatimPosition = 0
        this.verbatimSlots = 0
    }

    handleOpenDerivedChartModal = () => {
        this.derivedChartModalOpen = true
    }

    handleCloseDerivedChartModal = () => {
        this.derivedChartModalSelections = {
            question: '',
            kpi: '',
            base: 0,
        }
        this.derivedChartModalOpen = false
        this.editing = false
    }

    handleEditDerived = () => {
        const question = this.activeSlide.question.get(0)
        this.derivedChartModalSelections = {
            question: question?.questionId ?? '',
            kpi: question?.kpiQuestionId ?? '',
            base: question?.settings.removeBaseLimit ?? 0,
        }
        this.editing = true
        this.handleOpenDerivedChartModal()
    }

    handleEditCrossbreak = () => {
        const question = this.activeSlide.question.get(0)
        this.crossbreakChartModalSelections = {
            questions: question.questions.length
                ? question.questions.map((q) => ({
                    question: q.questionId ?? '',
                    ranking: q.ranking ?? 'noRanking',
                    base: q.removeBaseLimit ?? 0,
                }))
                : [
                    {
                        question: '',
                        ranking: 'noRanking',
                        base: 0,
                    },
                ],
            kpi: question.questionId ?? '',
            benchmarks: question.benchmark ?? [],
        }
        this.editing = true
        this.handleOpenCrossbreakSettingsModal()
    }

    handleEditInherited = () => {
        const question = this.activeSlide.question.get(0)
        this.inheritedChartModalSelections = {
            parent: question.questionId ?? '',
            child: R.pathOr('', [0, 'questionId'], question.questions) ?? '',
            ranking: question.settings.sorting,
            base: question.settings.removeBaseLimit,
            average: 'average',
            removeZero: question.settings.removeZero,
            locked: question.settings.locked ?? true,
            chartType: question.chart === 'inheritedStackedBar' ? 'inheritedStackedBar' : 'standardColumn',
            comparison: (question.comparison ?? []).slice(),
            comparisonYears: (question.comparisonYears ?? []).slice(),
        }
        this.editing = true
        this.handleOpenInheritedSettingsModal()
    }

    handleCrossbreakKpiSelection = (e) => {
        this.crossbreakChartModalSelections.kpi = e.value
    }

    handleCrossbreakSettingChange = (i, e) => {
        this.crossbreakChartModalSelections.questions[i][e.name] = e.value
    }

    handleAddCrossbreakSlide = async () => {
        this.recordHistory()
        const questions = this.crossbreakChartModalSelections.questions
            .map((q) => this.questions.find((qu) => qu.question.id === q.question))
        const kpi = this.questions.find((q) => q.question.id === this.crossbreakChartModalSelections.kpi)

        if (!this.editing) {
            this.activeSection.addSlide({
                template: 'crosstab',
                title: '',
            }, this.currentSlide, this.addPosition)
        }

        const {
            comparison,
        } = this.mapQuestionToChartDefaults(kpi)

        const slidePos = (this.editing || this.addPosition === 'above') ? this.currentSlide : this.currentSlide + 1

        this.activeSection.slide[slidePos].addQuestion(0, {
            surveyId: this.survey.surveyId,
            questionId: kpi.question.id,
            questionShortText: `${this.getTitle(kpi.question)} by subgroups`,
            kpiQuestionShortText: this.getTitle(kpi.question),
            questionLongText: `${this.getQuestionLongText(kpi.question)} vs subgroups`,
            questions: questions.map((q, i) => ({
                questionId: q.question.id,
                ranking: this.crossbreakChartModalSelections.questions[i].ranking,
                removeBaseLimit: parseInt(this.crossbreakChartModalSelections.questions[i].base, 10),
            })),
            respondentType: this.survey.respondentType,
            chart: 'bar',
            year: this.survey.year,
            benchmark: this.crossbreakChartModalSelections.benchmarks,
            comparison,
            settings: { ...settingsDefaults },
            position: 0,
            ppStore: this,
            store: this.appStore,
        })

        this.populatePresentationSurveyIds()
        await this.getCrossTabChartData(this.activeSection.slide[slidePos].getQuestionAtPosition(0))

        if (!this.editing) {
            this.changeCurrentSlide(this.currentSection, this.currentSlide + 1)

            if (this.addPosition === 'above') {
                this.changeCurrentSlide(this.currentSection, this.currentSlide)
            }
        }

        this.addPosition = null
        this.handleCloseCrossbreakSettingsModal()
    }

    handleAddDerivedSlide = async () => {
        this.recordHistory()
        const question = this.questions.find((q) => q.question.id === this.derivedChartModalSelections.question)
        const kpi = this.questions.find((q) => q.question.id === this.derivedChartModalSelections.kpi)
        if (!this.editing) {
            this.activeSection.addSlide({
                template: 'derived',
                title: '',
            }, this.currentSlide, this.addPosition)
        }

        const slidePos = (this.editing || this.addPosition === 'above') ? this.currentSlide : this.currentSlide + 1

        const ppQuestion = {
            questionId: question.question.id,
            questionShortText: this.getTitle(question.question),
            questionLongText: this.getQuestionLongText(question.question),
            kpiQuestionId: kpi.question.id,
            kpiQuestionShortText: this.getTitle(kpi.question),
            kpiQuestionLongText: this.getQuestionLongText(kpi.question),
            surveyId: this.survey.surveyId,
            respondentType: this.survey.respondentType,
            chart: 'quadrantAnalysis',
            settings: { ...settingsDefaults, removeBaseLimit: parseInt(this.derivedChartModalSelections.base, 10) },
            position: 0,
            year: this.survey.year,
            ppStore: this,
            store: this.appStore,
        }

        this.activeSection.slide[slidePos].addQuestion(0, ppQuestion)
        this.activeSection.slide[slidePos].updateTitle(
            { value: `Correlation Chart (${R.pathOr('', ['kpiQuestionShortText'], ppQuestion)} vs ${R.pathOr('', ['questionShortText'], ppQuestion)})`}
        )

        if (!this.editing) {
            this.changeCurrentSlide(this.currentSection, this.currentSlide + 1)

            if (this.addPosition === 'above') {
                this.changeCurrentSlide(this.currentSection, this.currentSlide)
            }
        }

        this.populatePresentationSurveyIds()
        await this.getDerivedChartData(ppQuestion)
        this.addPosition = null
        this.handleCloseDerivedChartModal()
    }

    handleAddInheritedSlide = async () => {
        this.recordHistory()
        const parent = this.questions.find((q) => q.question.id === this.inheritedChartModalSelections.parent)
        const child = this.questions.find((q) => q.question.id === this.inheritedChartModalSelections.child)
        const { comparison, comparisonYears } = this.inheritedChartModalSelections

        if (!this.editing) {
            this.activeSection.addSlide({
                template: 'inherited',
                title: '',
            }, this.currentSlide, this.addPosition)
        }

        const slidePos = (this.editing || this.addPosition === 'above') ? this.currentSlide : this.currentSlide + 1

        this.activeSection.slide[slidePos].addQuestion(0, {
            questionId: parent.question.id,
            surveyId: this.survey.surveyId,
            questionShortText: `${this.getTitle(parent.question)} / ${this.getTitle(child.question)}`,
            questionLongText: `${this.getQuestionLongText(parent.question)} / ${this.getQuestionLongText(child.question)}`,
            respondentType: this.survey.respondentType,
            questions: [{ questionId: child.question.id, ranking: 'noRanking', removeBaseLimit: parseInt(this.inheritedChartModalSelections.base, 10), removeZero: this.inheritedChartModalSelections.removeZero }],
            chart: this.inheritedChartModalSelections.chartType,
            benchmark: [],
            comparison,
            comparisonYears,
            settings: { ...settingsDefaults, sorting: this.inheritedChartModalSelections.ranking, removeBaseLimit: parseInt(this.inheritedChartModalSelections.base, 10), removeZero: this.inheritedChartModalSelections.removeZero, locked: this.inheritedChartModalSelections.locked },
            position: 0,
            ppStore: this,
            store: this.appStore,
        })
        await this.getInheritedChartData(parent, child)

        if (!this.editing) {
            this.changeCurrentSlide(this.currentSection, this.currentSlide + 1)

            if (this.addPosition === 'above') {
                this.changeCurrentSlide(this.currentSection, this.currentSlide)
            }
        }

        this.populatePresentationSurveyIds()
        this.addPosition = null
        this.handleCloseInheritedSettingsModal()
    }

    updateInheritedSettings = (e) => {
        this.inheritedChartModalSelections[e.name] = e.value
    }

    editSlideTitle = (e) => {
        this.activeSlide.title = e.value
        this.activeSlide.hasCustomTitle = !!e.value
        // Persist the title change via the store's debounced save.
        this.scheduleSave()
    }

    toggleExportWithHeadlines = (e) => {
        this.activeSlide.exportWithoutHeadlines = e.value
        // Persist the change via the store's debounced save.
        this.scheduleSave()
    }

    handleSearch = (e) => {
        this.search = e.value
        this.filterQuestions()
    }

    removeQuestionData = (surveyId, qid) => {
        this.questionData.delete(`${surveyId}-${qid}`)
    }

    onQuestionDragEnd = async (result) => {
        const destinationDropId = R.pathOr('', ['destination', 'droppableId'], result)

        const q = this.questions.find(
            (qu) => String(qu.pageQuestionId) === String(result.draggableId),
        )
        if (!q) return

        const {
            chart, sorting, benchmark, comparison, comparisonYears, hasNonNumericValues, hasNumericCodes, removeZero, locked, hasOtherOrExclusive, excludeNonNumeric, kpiType,
        } = this.mapQuestionToChartDefaults(q)

        if (destinationDropId === 'add-above') {
            if (!this.currentSection && !this.currentSlide) return

            this.recordHistory()

            this.activeSection.addSlide(
                {
                    template: ['TX', 'TA'].includes(q.question.questionTypeCode) ? 'verbatim' : '1x1',
                    title: '',
                },
                this.currentSlide,
                'above',
            )
            this.activeSection.slide[this.currentSlide].addQuestion(0, {
                questionId: q.question.id,
                pageQuestionId: q.pageQuestionId.toLowerCase(),
                questionShortText: this.getTitle(q.question),
                questionLongText: this.getQuestionLongText(q.question),
                respondentType: this.survey.respondentType,
                surveyId: this.survey.surveyId,
                chart,
                benchmark,
                comparison,
                comparisonYears,
                hasNonNumericValues,
                hasOtherOrExclusive,
                hasNumericCodes,
                year: this.survey.year,
                settings: {
                    ...settingsDefaults, sorting, removeZero, locked, excludeNonNumeric,
                },
                position: 0,
                ppStore: this,
                store: this.appStore,
                questionTypeCode: q.question.questionTypeCode,
                kpiType: kpiType ?? q.question.metadata.kpiType,
            })
            this.populatePresentationSurveyIds()
            this.changeCurrentSlide(this.currentSection, this.currentSlide + 1)
            this.changeCurrentSlide(this.currentSection, this.currentSlide - 1)
            const pptQuestion = this.activeSection.slide[this.currentSlide].getQuestionAtPosition(0)

            if (['TX', 'TA'].includes(q.question.questionTypeCode)) {
                await pptQuestion.fetchData()
            } else {
                await this.getQuestionData([q])
            }

            return
        }

        if (destinationDropId === 'add-below') {
            this.recordHistory()
            this.activeSection.addSlide(
                {
                    template: ['TX', 'TA'].includes(q.question.questionTypeCode) ? 'verbatim' : '1x1',
                    title: '',
                },
                this.currentSlide,
                'below',
            )

            this.activeSection.slide[this.currentSlide + 1].addQuestion(0, {
                questionId: q.question.id,
                pageQuestionId: q.pageQuestionId.toLowerCase(),
                surveyId: this.survey.surveyId,
                questionShortText: this.getTitle(q.question),
                questionLongText: this.getQuestionLongText(q.question),
                respondentType: this.survey.respondentType,
                chart,
                benchmark,
                comparison,
                comparisonYears,
                hasNonNumericValues,
                hasOtherOrExclusive,
                hasNumericCodes,
                year: this.survey.year,
                settings: {
                    ...settingsDefaults, sorting, removeZero, locked, excludeNonNumeric,
                },
                position: 0,
                ppStore: this,
                store: this.appStore,
                questionTypeCode: q.question.questionTypeCode,
                kpiType: kpiType ?? q.question.metadata.kpiType,
            })
            this.populatePresentationSurveyIds()
            this.changeCurrentSlide(this.currentSection, this.currentSlide + 1)

            const pptQuestion = this.activeSection.slide[this.currentSlide].getQuestionAtPosition(0)

            if (pptQuestion.isVerbatim) {
                await pptQuestion.fetchData()
            } else {
                await this.getQuestionData([q])
            }

            return
        }

        if (!destinationDropId.includes('slide-')) return

        const index = parseInt(destinationDropId.split('slide-')[1], 10)

        if (this.activeSlide.template !== '1x2' && ['TX', 'TA'].includes(q.question.questionTypeCode)) return

        const showAverage = this.activeSlide.template === '2x2' && ['RD', 'SD'].includes(q.question.questionTypeCode) && hasNumericCodes

        this.recordHistory()

        this.activeSlide.addQuestion(index, {
            questionId: q.question.id,
            pageQuestionId: q.pageQuestionId.toLowerCase(),
            questionShortText: this.getTitle(q.question),
            questionLongText: this.getQuestionLongText(q.question),
            respondentType: this.survey.respondentType,
            surveyId: this.survey.surveyId,
            chart: showAverage ? 'standardColumn' : chart,
            benchmark,
            comparison,
            comparisonYears,
            hasNonNumericValues,
            hasOtherOrExclusive,
            hasNumericCodes,
            year: this.survey.year,
            settings: {
                ...settingsDefaults, sorting, removeZero, locked, showAverage, excludeNonNumeric,
            },
            position: index,
            ppStore: this,
            store: this.appStore,
            questionTypeCode: q.question.questionTypeCode,
            kpiType: kpiType ?? q.question.metadata.kpiType,
        })
        this.populatePresentationSurveyIds()
        const pptQuestion = this.activeSection.slide[this.currentSlide].getQuestionAtPosition(index)

        if (['TX', 'TA'].includes(q.question.questionTypeCode)) {
            await pptQuestion.fetchData()
        } else {
            await this.getQuestionData([q])
        }
    }

    populatePresentationSurveyIds = () => {
        const surveyIds = new Set()

        this.presentation.section
            .forEach((s) => s.slide
                .forEach((sl) => sl.question
                    .forEach((q) => surveyIds.add(q.surveyId))))

        this.presentation.surveyId = Array.from(surveyIds)
    }

    getPresentationComparisonIds = () => {
        const comparisonIds = new Set()
        this.presentation.section
            .forEach((section) => section.slide
                .forEach((slide) => slide.question
                    .forEach((question) => (question.comparison || [])
                        .forEach((id) => id && comparisonIds.add(id)))))
        return Array.from(comparisonIds)
    }

    fetchSpecificationData = async () => {
        try {
            this.populatePresentationSurveyIds()
            const surveyId = this.presentation.surveyId.slice()
            const comparisonId = this.getPresentationComparisonIds()
            const resp = await this.appStore.http.post('/export/ppt/specification', JSON.stringify({
                surveyId,
                comparisonId,
            }))
            this.specificationData = resp ?? null
        } catch (e) {
            console.error('Failed to fetch specification data', e)
            this.specificationData = null
        }
    }

    onSlideDragEnd = (result) => {
        const destinationDropId = R.pathOr('', ['destination', 'droppableId'], result)
        const sourceDropId = R.pathOr('', ['source', 'droppableId'], result)
        if (destinationDropId.includes('slide-list-section-') && sourceDropId.includes('slide-list-section-')) {
            this.recordHistory()
            const destinationSection = parseInt(destinationDropId.split('slide-list-section-')[1], 10)
            const sourceSection = parseInt(sourceDropId.split('slide-list-section-')[1], 10)
            if (destinationSection === sourceSection) {
                this.presentation.section[destinationSection].moveSlide(result.source.index, result.destination.index)

                if (this.currentSlide === result.source.index) {
                    this.changeCurrentSlide(this.currentSection, result.destination.index)
                }
            } else {
                const slideToMove = this.presentation.section[sourceSection].slide[result.source.index]
                this.presentation.section[sourceSection].deleteSlide(result.source.index)
                this.presentation.section[destinationSection].addSlide(slideToMove, result.destination.index, 'below')

                if (!this.presentation.section[sourceSection].slide.length) {
                    if (this.currentSection === sourceSection) {
                        this.changeCurrentSlide(0, 0)
                    }

                    this.presentation.removeSection(sourceSection)
                    this.changeCurrentSlide(0, 0)
                }
            }
        }
    }

    addQuestionToCrossbreak = () => {
        this.crossbreakChartModalSelections.questions.push({
            question: '',
            ranking: 'noRanking',
            base: 0,
        })
    }

    handleRemoveQuestion = (pos) => {
        this.recordHistory()
        this.activeSlide.removeQuestion(pos)
        this.populatePresentationSurveyIds()
    }

    trackChartDeleted = (question) => {
        this.trackPresentation(MIXPANEL_EVENTS.PRESENTATION_CHART_DELETED, {
            chart_type: question?.chart,
            question_id: question?.questionId,
            question_type: question?.questionTypeCode,
            slide_template: this.activeSlide?.template,
        })
    }

    removeQuestionFromCrossbreak = (index) => {
        this.crossbreakChartModalSelections.questions = this.crossbreakChartModalSelections.questions
            .filter((q, i) => i !== index)
    }

    filterQuestions = () => {
        this.filteredQuestions = this.questions
            .filter((qu) => this.getTitle(qu.question).toLocaleLowerCase().includes(this.search.toLocaleLowerCase()))
    }

    toggleLayoutOpen = () => {
        this.slideLayoutOpen = !this.slideLayoutOpen
    }

    toggleSlidePreviewCollapsed = () => {
        this.slidePreviewCollapsed = !this.slidePreviewCollapsed
    }

    get canUndo() {
        return this.undoStack.length > 0
    }

    get canRedo() {
        return this.redoStack.length > 0
    }

    takeSnapshot = () => ({
        sections: this.presentation.section.map((section) => ({
            id: section.id,
            title: section.title,
            slide: section.slide.map((slide) => ({
                id: slide.id,
                title: slide.title,
                hasCustomTitle: slide.hasCustomTitle,
                template: slide.template,
                questions: Array.from(slide.question.entries()).map(([position, question]) => ({
                    position,
                    questionId: question.questionId,
                    kpiQuestionId: question.kpiQuestionId,
                    kpiQuestionShortText: question.kpiQuestionShortText,
                    kpiQuestionLongText: question.kpiQuestionLongText,
                    questionLongText: question.questionLongText,
                    questionShortText: question.questionShortText,
                    questionTypeCode: question.questionTypeCode,
                    kpiType: question.kpiType,
                    surveyId: question.surveyId,
                    year: question.year,
                    respondentType: question.respondentType,
                    chart: question.chart,
                    benchmark: toJS(question.benchmark),
                    comparison: toJS(question.comparison),
                    comparisonYears: toJS(question.comparisonYears),
                    questions: toJS(question.questions),
                    filter: toJS(question.filter),
                    hasNonNumericValues: question.hasNonNumericValues,
                    hasOtherOrExclusive: question.hasOtherOrExclusive,
                    hasNumericCodes: question.hasNumericCodes,
                    settings: toJS(question.settings),
                    questionCoding: Array.from(question.chosenCategories.entries())
                        .map(([slotPosition, { category, labels, pct }]) => ({
                            position: slotPosition,
                            name: category,
                            comments: toJS(labels),
                            pct,
                        })),
                })),
            })),
        })),
        currentSection: this.currentSection,
        currentSlide: this.currentSlide,
        currentChart: this.currentChart,
    })

    applySnapshot = (snapshot) => {
        this.applyingHistory = true
        try {
            this.presentation.section = snapshot.sections.map((secSnap) => {
                const section = new PPSection({ title: secSnap.title })
                section.id = secSnap.id
                section.slide = secSnap.slide.map((slideSnap) => {
                    const slide = new PPSlide({
                        title: slideSnap.title,
                        hasCustomTitle: slideSnap.hasCustomTitle,
                        template: slideSnap.template,
                    })
                    slide.id = slideSnap.id
                    slideSnap.questions.forEach((qSnap) => {
                        const question = new PPQuestion({
                            ...qSnap,
                            ppStore: this,
                            store: this.appStore,
                        })
                        slide.question.set(qSnap.position, question)
                    })
                    return slide
                })
                return section
            })
            this.currentSection = snapshot.currentSection
            this.currentSlide = snapshot.currentSlide
            this.currentChart = snapshot.currentChart
            this.refetchStaleChartData()
        } finally {
            this.applyingHistory = false
        }
    }

    refetchStaleChartData = () => {
        const stale = new Map()
        this.presentation.section.forEach((section) => {
            section.slide.forEach((slide) => {
                slide.question.forEach((ppQuestion) => {
                    const qid = ppQuestion.questionId
                    if (!qid) return
                    if (stale.has(qid)) return
                    const existing = this.chartData.get(`${ppQuestion.surveyId}-${qid}`)
                    if (!existing) return
                    const currentFilter = JSON.stringify(toJS(ppQuestion.filter) ?? null)
                    const fetchedFilter = JSON.stringify(existing.fetchedWithFilter ?? null)
                    if (currentFilter !== fetchedFilter) {
                        stale.set(qid, ppQuestion.filter)
                    }
                })
            })
        })

        if (stale.size === 0) return

        const matched = this.questions
            .filter(({ question }) => stale.has(question.id))
            .map((q) => ({ ...q, filter: stale.get(q.question.id) }))

        if (matched.length === 0) return
        this.getQuestionData(matched)
    }

    recordHistory = (snapshot = this.takeSnapshot()) => {
        if (this.applyingHistory) return
        const previous = this.undoStack[this.undoStack.length - 1]
        if (previous && JSON.stringify(previous) === JSON.stringify(snapshot)) return
        this.undoStack.push(snapshot)
        if (this.undoStack.length > HISTORY_LIMIT) this.undoStack.shift()
        this.redoStack = []
        this.scheduleSave()
    }

    undo = () => {
        if (!this.canUndo) return
        const current = this.takeSnapshot()
        const previous = this.undoStack.pop()
        this.redoStack.push(current)
        this.applySnapshot(previous)
        this.scheduleSave()
    }

    redo = () => {
        if (!this.canRedo) return
        const current = this.takeSnapshot()
        const next = this.redoStack.pop()
        this.undoStack.push(current)
        this.applySnapshot(next)
        this.scheduleSave()
    }

    scheduleSave = () => {
        if (this.isTemplate) return
        if (this.saveTimer) clearTimeout(this.saveTimer)
        this.saveTimer = setTimeout(() => {
            this.saveTimer = null
            this.handleSavePresentation()
        }, SAVE_DEBOUNCE_MS)
    }

    flushPendingSave = () => {
        if (this.isTemplate) return
        if (!this.saveTimer) return
        clearTimeout(this.saveTimer)
        this.saveTimer = null
        this.handleSavePresentation()
    }

    handleKeyboardListener = (dir) => {
        if (this.anyModalOpen) return
        console.log('THIS', this.presentation, this.currentSection, this.currentSlide)
        if (dir < 0) {
            if (this.currentSection === 0 && this.currentSlide === 0) return null
            if (this.currentSection === 0 && this.currentSlide !== 0) {
                return this.changeCurrentSlide(0, this.currentSlide - 1)
            }
            if (this.currentSection !== 0 && this.currentSlide === 0) {
                return this.changeCurrentSlide(
                    this.currentSection - 1,
                    this.presentation.section[this.currentSection - 1].slide.length - 1,
                )
            }
            return this.changeCurrentSlide(this.currentSection, this.currentSlide - 1)
        }
        if (dir > 0) {
            if (this.currentSection === (this.presentation.section.length - 1)
                && this.currentSlide === (this.presentation.section[this.currentSection].slide.length - 1)) return null

            if (this.currentSlide === (this.presentation.section[this.currentSection].slide.length - 1)) {
                return this.changeCurrentSlide(this.currentSection + 1, 0)
            }

            return this.changeCurrentSlide(this.currentSection, this.currentSlide + 1)
        }
    }

    changeCurrentSlide = (section, slide) => {
        if (this.currentSection !== section) this.currentSection = section
        this.currentSlide = slide
        this.openFilterPopoverPosition = null
        this.filterQuestions()
    }

    handleSelectSurvey = async (e) => {
        this.survey = this.surveys.find((surv) => surv.surveyId === e.value)
        this.filteredQuestions = this.questions
        await this.getFullSurvey()
        await this.getSurveySkipLogic()
        await this.getMergefielsdForSurvey()
        await this.getQuestionsForSurvey()
        await this.getQuestionBenchmarksForSurvey()
        await this.getQuestionYearsForSurvey()
        await this.getQuestionCodingAvailabilityForSurvey()
        this.getSurveyCodingAvailability()
    }

    getFullSurvey = async () => {
        if (this.fullSurveys.has(this.survey.surveyId.toUpperCase())) return

        const survey = await this.appStore.http.get(`/survey/${this.survey.surveyId}`)

        if (!survey) return

        this.fullSurveys.set(this.survey.surveyId.toUpperCase(), survey)
    }

    getSurveySkipLogic = async () => {
        const key = this.survey.surveyId.toUpperCase()
        if (this.skipLogicAdapters.has(key)) return
        const [survey, questions] = await Promise.all([
            this.appStore.http.get(`/api/survey/${this.survey.surveyId}`),
            this.appStore.http.get(`/api/survey/${this.survey.surveyId}/questions`),
        ])

        if (!survey) return

        this.skipLogicAdapters.set(key, buildSkipLogicAdapter(survey, questions))
    }

    getSkipLogicAdapter = (surveyId) => {
        const key = surveyId?.toUpperCase?.() ?? surveyId
        return this.skipLogicAdapters.get(key) ?? null
    }

    getSkipLogicForQuestion = (surveyId, pageQuestionId) => {
        const adapter = this.getSkipLogicAdapter(surveyId)
        if (!adapter || !pageQuestionId) return []
        const target = String(pageQuestionId).toUpperCase()
        return adapter.skipLogic.filter((sl) => {
            const action = R.pathOr({}, ['action'], sl)
            return ['ShowQuestion', 'HideQuestion', 'ShowQuestionOption'].includes(action.kind)
                && String(action.pageQuestionId).toUpperCase() === target
        })
    }

    hasSkipLogic = (surveyId, pageQuestionId) => (
        !!surveyId && this.getSkipLogicForQuestion(surveyId, pageQuestionId).length > 0
    )

    getQuestionCodingAvailabilityForSurvey = async () => {
        if (this.questionCodingAvailability.has(this.survey.surveyId.toUpperCase())) return

        const survey = this.fullSurveys.get(this.survey.surveyId.toUpperCase())

        if (!survey) return

        survey.survey.pages.flatMap((page) => page.questions).forEach((q) => {
            if (q) {
                this.questionCodingAvailability.set(q.id, !q.isAiCodingDisabled || false)
            }
        })
    }

    getSurveyCodingAvailability = () => {
        if (this.surveyCodingAvailability.has(this.survey.surveyId.toUpperCase())) return

        const survey = this.fullSurveys.get(this.survey.surveyId.toUpperCase())

        if (!survey) return

        this.surveyCodingAvailability.set(this.survey.surveyId.toUpperCase(),
            survey.survey.metadata.isSurveyAiSummaryEnabled)
    }

    getQuestionBenchmarksForSurvey = async () => {
        if (this.questionBenchmarks.has(this.survey.surveyId.toUpperCase())) return

        const benchmarks = await this.getBenchmarks(this.survey.surveyId)

        this.questionBenchmarks.set(this.survey.surveyId.toUpperCase(), benchmarks)
    }

    getQuestionYearsForSurvey = async () => {
        if (this.questionYears.has(this.survey.surveyId.toUpperCase())) return

        const years = await this.getQuestionYears(this.survey.surveyId)

        this.questionYears.set(this.survey.surveyId.toUpperCase(), years)
    }

    handleCloseSideControls = () => {
        this.sideNavigationOpen = ''
    }

    editSectionTitle = (e) => {
        this.presentation?.section[this.editingSection].updateTitle(e)
    }

    handleEditChart = (i) => {
        this.currentChart = i
        this.search = ''
        this.handleOpenChartSettingsModal()
    }

    getTagBenchmarks = (benchmarkTags) => {
        const { langcode } = this.appStore
        return benchmarkTags.flatMap(({
            customTagName, customTagId, customTagConfig, tagOptions, parentConfig,
        }) => {
            const config = parentConfig || customTagConfig
            const parsedConfig = process.env.NEW_CUSTOM_BMTAGS_ENDPOINT ? config : JSON.parse(config)
            const scopes = parsedConfig.benchmarkOptions.filter((scope) => scope !== 'publish')

            return scopes.flatMap((scope) => (
                tagOptions.map(({ tagOptionId, tagOptionConfig }) => {
                    const toc = process.env.NEW_CUSTOM_BMTAGS_ENDPOINT ? tagOptionConfig : JSON.parse(tagOptionConfig)
                    const tagName = R.pathOr(
                        R.pathOr(
                            R.pathOr('', ['langs', 'en_GB'], toc),
                            ['langs', langcode],
                            toc,
                        ),
                        ['name'], toc,
                    )
                    return {
                        tagOptionId,
                        customTag: customTagId,
                        customTagName,
                        scope,
                        shortLabel: tagName,
                        value: `${scope}_${tagOptionId}`,
                        label: `${scope} (${customTagName} - ${tagName})`,
                    }
                })
            ))
        })
    }

    handleAddSpecificationQuestion = () => {
        this.recordHistory()

        this.activeSection.addSlide({
            template: 'specification', title: 'Project Specifications',
        }, this.currentSlide, this.addPosition)
    }

    handleAddLMXQuestion = async () => {
        this.recordHistory()

        this.activeSection.addSlide({
            template: 'lmx', title: 'Satisfaction vs Loyalty',
        }, this.currentSlide, this.addPosition)

        const comparisonYears = new Set()
        const comparison = new Set()

        Object.entries(this.globalYears).forEach(([year, data]) => {
            if (data.map((d) => d.checked).every((v) => !!v) && year < this.survey.year) {
                comparisonYears.add(Number(year))
                data.forEach((d) => comparison.add(d.value))
            }
        })

        const benchmark = this.globalBenchmarks
            .filter((b) => b.checked)
            .map((b) => b.value)

        this.activeSection.slide[this.currentSlide + 1].addQuestion(0, {
            questionId: '00000000-0000-0000-0000-000000000000',
            surveyId: this.survey.surveyId,
            questionShortText: '',
            questionLongText: '',
            respondentType: this.survey.respondentType,
            chart: 'lmx',
            benchmark,
            comparison: Array.from(comparison),
            comparisonYears: Array.from(comparisonYears),
            hasNonNumericValues: false,
            hasOtherOrExclusive: false,
            hasNumericCodes: false,
            year: this.survey.year,
            settings: { ...settingsDefaults, sorting: 'none', removeZero: false, locked: false },
            position: 0,
            ppStore: this,
            store: this.appStore,
            questionTypeCode: '',
            kpiType: 'LMX',
        })

        await this.fetchLMXChartData()
        this.populatePresentationSurveyIds()
        this.changeCurrentSlide(this.currentSection, this.currentSlide + 1)
        this.handleCloseSlideSelectModal()
    }

    handleAddOssQuestion = async () => {
        this.recordHistory()

        this.activeSection.addSlide({
            template: 'oss', title: 'Overall Show Score',
        }, this.currentSlide, this.addPosition)

        const comparisonYears = new Set()
        const comparison = new Set()

        Object.entries(this.globalYears).forEach(([year, data]) => {
            if (data.map((d) => d.checked).every((v) => !!v) && year <= this.survey.year) {
                comparisonYears.add(Number(year))
                data.forEach((d) => comparison.add(d.value))
            }
        })

        const benchmark = this.globalBenchmarks
            .filter((b) => b.checked)
            .map((b) => b.value)

        this.activeSection.slide[this.currentSlide + 1].addQuestion(0, {
            questionId: '00000000-0000-0000-0000-000000000000',
            surveyId: this.survey.surveyId,
            questionShortText: '',
            questionLongText: '',
            respondentType: this.survey.respondentType,
            chart: 'standardColumn',
            benchmark,
            comparison: Array.from(comparison),
            comparisonYears: Array.from(comparisonYears),
            hasNonNumericValues: false,
            hasOtherOrExclusive: false,
            hasNumericCodes: false,
            year: this.survey.year,
            settings: { ...settingsDefaults, sorting: 'none', removeZero: false, locked: false },
            position: 0,
            ppStore: this,
            store: this.appStore,
            questionTypeCode: '',
            kpiType: 'OSS',
        })

        await this.fetchOSSChartData()
        this.populatePresentationSurveyIds()
        this.changeCurrentSlide(this.currentSection, this.currentSlide + 1)
        this.handleCloseSlideSelectModal()
    }

    compareValues = (value1, value2) => {
        const v1 = value1 ? Number(value1) : 0
        const v2 = value2 ? Number(value2) : 0

        if (v1 > v2) {
            return { className: 'gt', icon: '↑' }
        }
        if (v1 < v2) {
            return { className: 'lt', icon: '↓' }
        }
        if (v2 === v1) {
            return { className: 'eq', icon: '↔' }
        }
        return { className: '', icon: '' }
    }

    addSection = () => {
        this.recordHistory()
        this.presentation.addSection({
            title: 'New Section',
            slide: [
                {
                    template: 'sectionHeader',
                    title: 'New Section',
                },
            ],
        }, this.presentation.section.length - 1, 'below')
        this.trackPresentation(MIXPANEL_EVENTS.PRESENTATION_SECTION_ADDED)
    }

    handleAddSlide = async (template) => {
        if (template === 'derived') {
            this.handleOpenDerivedChartModal()
        } else if (template === 'inherited') {
            this.handleOpenInheritedSettingsModal()
        } else if (template === 'crosstab') {
            this.handleOpenCrossbreakSettingsModal()
        } else if (template === 'oss') {
            await this.handleAddOssQuestion()
        } else if (template === 'lmx') {
            await this.handleAddLMXQuestion()
        } else if (template === 'specification') {
            await this.handleAddSpecificationQuestion()
        } else if (template === 'sectionHeader') {
            this.recordHistory()
            this.presentation.addSection({
                slide: [
                    {
                        template, title: '',
                    },
                ],
            }, this.currentSection, this.addPosition)

            this.changeCurrentSlide(this.currentSection + 1, 0)

            if (this.addPosition === 'above') {
                this.changeCurrentSlide(this.currentSection - 1, 0)
            }
        } else {
            this.recordHistory()
            const title = ['swot', 'summary'].includes(template) ? 'Summary' : ''
            this.activeSection.addSlide({
                template, title,
            }, this.currentSlide, this.addPosition)

            this.changeCurrentSlide(this.currentSection, this.currentSlide + 1)

            if (this.addPosition === 'above') {
                this.changeCurrentSlide(this.currentSection, this.currentSlide - 1)
            }
        }

        this.handleCloseSlideSelectModal()
    }

    deleteSlide = (source = 'slide') => {
        if (this.currentSection === 0
                && this.presentation.section.length === 1
                && this.activeSection.slide.length === 1) return

        this.recordHistory()

        this.trackPresentation(MIXPANEL_EVENTS.PRESENTATION_SLIDE_DELETED, {
            source,
            slide_template: this.activeSlide?.template,
            removes_section: this.activeSection?.slide.length === 1,
        })

        const slideToDelete = this.currentSlide

        if (this.activeSection?.slide.length === 1) {
            const sectionToDelete = this.currentSection
            this.changeCurrentSlide(this.currentSection - 1,
                this.presentation.section[this.currentSection].slide.length - 1)
            this.presentation.removeSection(sectionToDelete)
            return
        }

        if (this.activeSection.slide[this.currentSlide - 1]) {
            this.changeCurrentSlide(this.currentSection, this.currentSlide - 1)
        }

        if (this.currentSlide === 0) {
            this.changeCurrentSlide(this.currentSection, 0)
        }

        this.activeSection.deleteSlide(slideToDelete)
    }

    duplicateSlide = () => {
        if (!this.activeSlide || NON_DUPLICABLE_SLIDE_TEMPLATES.includes(this.activeSlide.template)) return

        this.recordHistory()

        this.trackPresentation(MIXPANEL_EVENTS.PRESENTATION_SLIDE_CLONED, {
            slide_template: this.activeSlide.template,
            question_count: this.activeSlide.question?.size ?? 0,
        })

        const sourceSlide = this.activeSlide
        const sourcePosition = this.currentSlide

        this.activeSection.addSlide({
            template: sourceSlide.template,
            title: sourceSlide.title,
        }, sourcePosition, 'below')

        const newSlide = this.activeSection.slide[sourcePosition + 1]

        sourceSlide.question.forEach((q, position) => {
            newSlide.addQuestion(position, {
                ...cloneDeep(toJS(q.toJSON())),
                kpiType: q.kpiType,
                hasOtherOrExclusive: q.hasOtherOrExclusive,
                hasNumericCodes: q.hasNumericCodes,
                ppStore: this,
                store: this.appStore,
            })
        })

        this.populatePresentationSurveyIds()
        this.changeCurrentSlide(this.currentSection, sourcePosition + 1)
    }

    getTitle = (question) => {
        const initialLocale = R.pathOr('en_GB', ['metadata', 'initialLocale'], question)
        const questionContent = R.pathOr({}, ['content', 'shortTitle'], question)
        const locale = this.fullSurveys.get(this.survey.surveyId)?.survey.metadata.defaultLocale
        const title = R.pathOr('', ['text'], getQuestionTitle(
            {
                content: questionContent,
                locale,
                defaultLocale: initialLocale,
                initialLocale: locale,
            },
        ))

        return MergeFields.replace(title, locale, this.mergefields)
    }

    getQuestionLongText = (question) => {
        const locale = this.fullSurveys.get(this.survey.surveyId)?.survey.metadata.defaultLocale
        const initialLocale = R.pathOr('en_GB', ['metadata', 'initialLocale'], question)
        const questionContent = R.pathOr({}, ['content', 'longTitle'], question)
        const longText = R.pathOr('', ['text'], getQuestionTitle(
            {
                content: questionContent,
                locale,
                defaultLocale: initialLocale,
                initialLocale: locale,
            },
        ))

        return MergeFields.replace(longText, locale, this.mergefields)
    }

    getMergefielsdForSurvey = async () => {
        this.mergefields = await this.appStore.http.get(`/mergefields/survey/${this.survey.surveyId}`)
    }

    getQuestionsForSurvey = async () => {
        const questions = await this.appStore.http.get(`/api/survey/${this.survey.surveyId}/questions`)
        this.questions = questions.filter((q) => (
            !['I', 'DT', 'TL', 'UV', 'ST', 'SG'].includes(q.question.questionTypeCode)
            && !(this.isTemplate && [6, 7].includes(R.pathOr(null, ['question', 'metadata', 'oid'], q)))
        ))
        this.filteredQuestions = this.questions
        this.mapSurveyFilterQuestions(this.survey.surveyId, this.questions)
    }

    mapSurveyFilterQuestions = (surveyId, questions) => {
        const locale = this.fullSurveys.get(this.activeSurvey.id).survey.metadata.defaultLocale
        const pageQuestions = questions
            .filter(({ question }) => (
                !['ST', 'TA', 'TX', 'TL', 'UV', 'I', 'OD', 'DT'].includes(question.questionTypeCode)
            ))
            .map(({ question }) => {
                const { questionTypeCode } = question
                const initialLocale = R.pathOr('en_GB', ['metadata', 'initialLocale'], question)

                const titleObj = getQuestionTitle({
                    content: R.pathOr({}, ['content', 'shortTitle'], question),
                    formContent: R.pathOr(null, ['formContent', 'shortTitle'], question),
                    locale,
                    defaultLocale: locale,
                    initialLocale,
                })
                const label = R.pathOr(titleObj, ['text'], titleObj)

                const baseOptionSet = question.optionSet || question.optionSetY || { options: [] }
                let options = baseOptionSet.options.map((o) => {
                    const optionLabel = getOptionTitle({
                        langs: o.langs,
                        locale,
                        defaultLocale: locale,
                        initialLocale,
                    })
                    return {
                        id: `${question.id}_${o.id}`,
                        label: R.pathOr(optionLabel, ['text'], optionLabel),
                        setYId: o.id,
                    }
                })

                if (['SG', 'TB', 'RT'].includes(questionTypeCode)) {
                    const setXOptions = R.pathOr([], ['optionSetX', 'options'], question)
                        .map((o) => {
                            const optionLabel = getOptionTitle({
                                langs: o.langs,
                                locale,
                                defaultLocale: locale,
                                initialLocale,
                            })
                            return {
                                label: R.pathOr(optionLabel, ['text'], optionLabel),
                                setXId: o.id,
                            }
                        })

                    options = options.flatMap(({ label: yLabel, setYId }) => (
                        setXOptions.map((optionX) => ({
                            id: `${question.id}_${setYId}_${optionX.setXId}`,
                            label: `${yLabel} - ${optionX.label}`,
                            setYId,
                            setXId: optionX.setXId,
                        }))
                    ))
                }

                return {
                    label,
                    id: question.id,
                    questionTypeCode,
                    options,
                }
            })

        this.surveyFilterQuestions.set(surveyId.toUpperCase(), [{
            title: 'Survey questions',
            pageQuestions,
        }])
    }

    addFilterRule = (ruleGroup, label, id, rule, questionTypeCode) => {
        const logic = ['DT'].includes(questionTypeCode) ? 'AND' : 'OR'

        ruleGroup.addRule(new Rule({
            id,
            label,
            options: [rule],
            logic,
            questionTypeCode,
        }), questionTypeCode === 'DT')
    }

    mapQuestionToChartDefaults = (q) => {
        const { question, question: { questionTypeCode } } = q
        let hasNumericCodes = false
        let hasOtherOrExclusive = false
        let hasNonNumericValues = false

        if (questionTypeCode === 'RD') {
            hasNumericCodes = !!(question.optionSet.hasNumericCodes)
            hasOtherOrExclusive = question.optionSet.options.some(({ hasOptionOther }) => hasOptionOther)
            if (!isEmpty(question.metadata.kpiType)) {
                const { kpiType: questionKpiType } = question.metadata
                this.kpis[questionKpiType] = true
            }
        }

        if (questionTypeCode === 'CH') {
            hasOtherOrExclusive = question.optionSet.options.some(({ hasOptionOther, isExclusive }) => hasOptionOther || isExclusive)
        }

        if (questionTypeCode === 'TB') {
            hasNumericCodes = !!(question.optionSetX?.hasNumericCodes)
            const nonNumericOptions = (ele) => !ele.hasOwnProperty('reportingValue')
            hasNonNumericValues = question.optionSetX.options.some(nonNumericOptions)
        }

        const defaults = this.getChartDefaults(question, hasNumericCodes, hasOtherOrExclusive)
        const comparison = new Set()
        const comparisonYears = new Set()
        const benchmark = this.getQuestionBenchmarks(question.id, this.survey.surveyId).filter((b) => b.checked).map((b) => b.value)
        if (this.questionYears.get(this.survey.surveyId)[question.id]) {
            Object.entries(this.globalYears).forEach(([year, data]) => {
                if (data.map((d) => d.checked).every((v) => !!v)) {
                    const globalSurveyIds = data.map((d) => d.value)
                    this.questionYears.get(this.survey.surveyId)[question.id].forEach((qy) => {
                        if (globalSurveyIds.includes(qy.value)) {
                            comparison.add(qy.value)
                        }
                    })
                    comparisonYears.add(Number(year))
                }
            })
        }
        return {
            chart: defaults.chartType,
            sorting: defaults.sorting,
            removeZero: defaults.removeZero ?? false,
            locked: defaults.lock ?? false,
            excludeNonNumeric: hasNumericCodes
                && hasNonNumericValues
                && ['stackedBar', 'stackedColumn'].includes(defaults.chartType),
            benchmark,
            hasNumericCodes,
            hasOtherOrExclusive,
            hasNonNumericValues,
            comparison: Array.from(comparison),
            comparisonYears: Array.from(comparisonYears),
            kpiType: defaults.kpiType,
        }
    }

    formatData = (data, element, surveyYear) => {
        if (!data || !element.dataDetails) return {}
        const productFilters = element.scope === 'product' ? R.pathOr([], ['filter'], element).map((f) => f.productId) : []
        const portfolioFilters = element.scope === 'portfolio' ? R.pathOr([], ['filter'], element).map((f) => f.portfolioId) : []
        const divisionFilters = element.scope === 'division' ? R.pathOr([], ['filter'], element).map((f) => f.divisionId) : []
        const allowedBenchmarks = !['treemap', 'pie', 'doughnut'].includes(element.chartType)
        const elementBenchmarks = R.pathOr(this.possibleBenchmarks[6], ['widgetRenderSettings', 'allowedBenchmarks'], element)
        const filteredData = data.filter((d) => (
            this.benchmarkCheck(d, allowedBenchmarks, elementBenchmarks)
            && this.productFilterCheck(d, productFilters)
            && this.portfolioFilterCheck(d, portfolioFilters)
            && this.divisionFilterCheck(d, divisionFilters)
        ))
        return this.dataFormatter?.formatData(filteredData, element, surveyYear)
    }

    benchmarkCheck = (dataObj, allowedBenchmarks, elementBenchmarks) => {
        const { benchmarkPeriod } = this
        return true
        if (!dataObj.benchmark) return true
        if (!allowedBenchmarks) return false
        if (dataObj.benchmarkPeriod !== benchmarkPeriod) return false
        if (!elementBenchmarks.includes(dataObj.benchmark)) return false
        if (dataObj.tagOptionId !== 0) return Boolean(this.benchmarks.get(`${dataObj.benchmark === 'client' ? 'company' : dataObj.benchmark}_${dataObj.tagOptionId}`))
        if (dataObj.termId) return Boolean(this.benchmarks.get('sector'))
            return true
        return this.benchmarks.has(dataObj.benchmark)
            ? this.benchmarks.get(dataObj.benchmark) : true
    }

    productFilterCheck = (dataObj, productFilters) => {
        const { productId, benchmark } = dataObj
        if (!productFilters.length) return true
        return productFilters.includes(productId) || benchmark
    }

    portfolioFilterCheck = (dataObj, portfolioFilters) => {
        const { portfolioId, benchmark } = dataObj
        if (!portfolioFilters.length) return true
        return portfolioFilters.includes(portfolioId) || benchmark
    }

    divisionFilterCheck = (dataObj, divisionFilters) => {
        const { divisionId, benchmark } = dataObj
        if (!divisionFilters.length) return true
        return divisionFilters.includes(divisionId) || benchmark
    }

    getChartScope = (oid) => {
        switch (oid) {
            case 3:
                return 'client'
            case 4:
                return 'division'
            case 5:
                return 'portfolio'
            case 6:
                return 'event'
            default:
                return 'NoOID'
        }
    }

    handleHighlightOptions = (options, chart, sortedOptionIds) => {
        const obj = {}

        options.forEach(({ series, index }) => {
            // `index` is the chart's sorted display position, so resolve the option
            // id via the sorted list rather than the raw options order.
            const optionId = sortedOptionIds[index]
            if (optionId == null) return
            if (obj[series]) {
                obj[series].push(optionId)
            } else {
                obj[series] = [optionId]
            }
        })

        chart.setHighlightedOptions(obj)
        // Persist highlight changes via the store's debounced save, so rapid
        // toggles coalesce into a single save.
        this.scheduleSave()
    }

    // Ordering for the "Lock Other/Exclusive" feature. `items` is already sorted per
    // the chart's sorting setting, so normal options keep their sorted order, then
    // Other options are appended, then Exclusive options (also in sorted order).
    // Falls back to treating any legacy combined labels as "Other" when the split
    // sets are unavailable.
    lockedOtherExclusiveOrder = (element, items) => {
        const otherLabels = element.otherLabels ?? element.otherOrExclusiveLabels ?? new Set()
        const exclusiveLabels = element.exclusiveLabels ?? new Set()
        const normalIndices = []
        const otherIndices = []
        const exclusiveIndices = []
        items.forEach((d, i) => {
            const name = d?.name
            if (name != null && otherLabels.has(name)) otherIndices.push(i)
            else if (name != null && exclusiveLabels.has(name)) exclusiveIndices.push(i)
            else normalIndices.push(i)
        })
        return {
            reorderedIndices: [...normalIndices, ...otherIndices, ...exclusiveIndices],
            hasReorder: otherIndices.length > 0 || exclusiveIndices.length > 0,
        }
    }

    getChart = (element, data, yearFilters) => {
        let noData = !data || (Array.isArray(data) && data.length === 0)
        let formattedData
        const { questionTypeCode, settings } = element
        const survey = this.surveys.find(({ surveyId }) => surveyId.toUpperCase() === element.surveyId.toUpperCase())
        const { sorting, removeZero } = settings
        if (noData) return
        if (element.chartType !== 'quadrantAnalysis' && !['crossbreak', 'inherited'].includes(element.kpiType)) {
            if (!['LMX', 'OSS'].includes(element.kpiType)) {
                const currentYear = Number(survey?.year)
                if (Number.isFinite(currentYear)) {
                    const hasCurrentYearData = Array.isArray(data)
                        && data.some((d) => !d.benchmarkName && Number(d.year) === currentYear)
                    if (!hasCurrentYearData) return null
                }
            }

            const yearFilteredData = data?.filter(
                (d) => yearFilters?.includes(d.year) || d.year === element.surveyYear || !!d.benchmarkName,
            )

            formattedData = this.formatData(
                yearFilteredData, element, survey?.year,
            )

            if (!formattedData) return

            if (settings.showAverage && questionTypeCode !== 'TB') {
                const averageLabel = R.pathOr('Average', ['averages', 'xAxisLabels', 0], element)
                const allowedYears = new Set([...(yearFilters || []), element.surveyYear].map(Number))
                const yearSeries = (element.averages?.fData ?? []).map((d) => ({
                    data: [...d.data],
                    name: d.name,
                    year: d.year ?? d.name,
                })).filter(({ name }) => allowedYears.has(Number(name)))

                const qbench = this.questionBenchmarks?.get(element.surveyId)?.[this.activePeriodKey]
                    ?.benchmarks?.[element.questionId]
                const selectedBenchmarks = R.pathOr([], ['benchmark'], this.activeSlide?.question?.get(element.position ?? this.currentChart))
                const benchmarkSeries = qbench
                    ? selectedBenchmarks.map((key) => {
                        const value = element.chartType === 'npsChart' ? qbench[key]?.nps : qbench[key]?.average
                        if (value == null) return null
                        return {
                            name: capitalize(key),
                            benchmark: key,
                            benchmarkName: capitalize(key),
                            data: [{ value, name: averageLabel }],
                        }
                    }).filter(Boolean)
                    : []

                formattedData.fData = [...yearSeries, ...benchmarkSeries]
                formattedData.xAxisLabels = (element.averages?.xAxisLabels ?? []).slice()
            }

            if (formattedData.fData) {
                const isStandardBar = element.chartType === 'standardBar'
                if (isStandardBar) {
                    formattedData.fData = formattedData.fData.map((series) => (
                        { ...series, data: series.data.slice().reverse() }
                    ))
                }

                let yearData = (questionTypeCode === 'TB'
                    ? formattedData.fData?.[0]?.data.map(({ name, avg, value: v }) => {
                        let value = avg ?? 0
                        if (settings.showAverage) value = avg ?? v
                        return { name, value }
                    })
                    : formattedData.fData.find(({ name }) => name === survey?.year)?.data
                ) ?? []

                if (settings.showAverage && questionTypeCode !== 'TB') {
                    yearData = formattedData.fData.map(({ data: [{ value }], name }) => ({
                        value,
                        name,
                    }))
                }

                const tbRowTotals = questionTypeCode === 'TB' && !settings.showAverage
                    ? (formattedData.fData?.[0]?.data ?? []).map((_, i) => (
                        formattedData.fData.reduce((sum, series) => (
                            sum + Math.abs(Number(series?.data?.[i]?.value) || 0)
                        ), 0)
                    ))
                    : null

                let mappedYearData
                switch (sorting) {
                    case 'highToLow':
                    case 'lowToHigh':
                        mappedYearData = yearData.map((d) => d.value)
                        break
                    case 'aToZ':
                    case 'zToA':
                        mappedYearData = yearData.map((d) => d.name)
                        break
                    default:
                        mappedYearData = yearData.map((_, i) => i)
                }

                let indexArray = sortedIndexArray(mappedYearData).filter((i) => {
                    if (!removeZero) return true
                    if (tbRowTotals) return tbRowTotals[i] !== 0
                    return yearData[i].value !== 0
                })
                if (['highToLow', 'zToA'].includes(sorting)) {
                    indexArray = indexArray.reverse()
                }

                const { optionsDisplayed } = settings
                if (
                    ['CH', 'RD', 'SB'].includes(questionTypeCode)
                    && !settings.showAverage
                    && Number.isFinite(optionsDisplayed)
                    && optionsDisplayed > 0
                ) {
                    indexArray = indexArray.slice(0, optionsDisplayed)
                }

                if (indexArray.length > 0 && !settings.showAverage) {
                    if (['TB', 'SG'].includes(questionTypeCode)) {
                        indexArray = indexArray.reverse()
                        formattedData?.fData?.map((fData) => {
                            if (Array.isArray(fData?.data)) {
                                const b = fData.data.slice()
                                fData.data = indexArray.map((i) => b[i])
                            }
                        })
                    } else {
                        formattedData.fData.map((data) => {
                            const b = data.data.slice()
                            data.data = indexArray.map((i) => b[i])
                        })
                    }
                    const labels = formattedData.xAxisLabels.slice()
                    formattedData.xAxisLabels = indexArray.map((i) => MergeFields.replace(labels[i], this.appStore.locale, this.mergefields))
                } else if (settings.showAverage) {
                    const data = formattedData.fData.slice()
                    formattedData.fData = indexArray.map((i) => data[i])
                }

                if (isStandardBar) {
                    formattedData.fData = formattedData.fData.map((series) => (
                        { ...series, data: series.data.slice().reverse() }
                    ))
                }

                if (settings.locked && element.otherOrExclusiveLabels?.size > 0) {
                    if (isStandardBar) {
                        formattedData.fData = formattedData.fData.map((series) => (
                            { ...series, data: series.data.slice().reverse() }
                        ))
                    }

                    const firstSeriesData = formattedData.fData[0]?.data ?? []
                    const { reorderedIndices, hasReorder } = this
                        .lockedOtherExclusiveOrder(element, firstSeriesData)
                    if (hasReorder) {
                        formattedData.fData = formattedData.fData.map((series) => {
                            const b = series.data.slice()
                            return { ...series, data: reorderedIndices.map((i) => b[i]) }
                        })
                        const sortedLabels = formattedData.xAxisLabels.slice()
                        formattedData.xAxisLabels = reorderedIndices.map((i) => sortedLabels[i])
                    }

                    if (isStandardBar) {
                        formattedData.fData = formattedData.fData.map((series) => (
                            { ...series, data: series.data.slice().reverse() }
                        ))
                    }
                }
            }
        } else if (element.kpiType === 'crossbreak') {
            formattedData = {
                fData: data,
                lineData: element.lineData,
                xAxisLabels: data[0].data.map((v) => v.name),
            }
        } else if (element.kpiType === 'inherited') {
            let mappedData
            switch (sorting) {
                case 'highToLow':
                case 'lowToHigh':
                    mappedData = data[0].data.map((d) => d.value)
                    break
                case 'aToZ':
                case 'zToA':
                    mappedData = data[0].data.map((d) => d.name)
                    break
                default:
                    mappedData = data[0].data.map((_, i) => i)
            }

            const removeBaseLimit = settings?.removeBaseLimit || 0
            let indexArray = sortedIndexArray(mappedData).filter((i) => {
                const passesZero = !removeZero || data[0].data[i].value !== 0
                const childBase = data[0].data[i].childBase
                const passesBase = !removeBaseLimit || childBase == null || childBase >= removeBaseLimit
                return passesZero && passesBase
            })
            if (['highToLow', 'zToA'].includes(sorting)) {
                indexArray = indexArray.reverse()
            }

            const labels = data[0].data.map((v) => v.name)
            const lineData = { ...element.lineData[0] }
            const sortedData = [...data]
            sortedData[0].data = indexArray.map((i) => data[0].data[i])
            lineData.data = indexArray.map((i) => lineData.data[i])
            let sortedLabels = indexArray
                .map((i) => MergeFields.replace(labels[i], this.appStore.locale, this.mergefields))

            if (settings.locked && element.otherOrExclusiveLabels?.size > 0) {
                const { reorderedIndices, hasReorder } = this
                    .lockedOtherExclusiveOrder(element, sortedData[0].data)
                if (hasReorder) {
                    const barData = sortedData[0].data.slice()
                    sortedData[0].data = reorderedIndices.map((i) => barData[i])
                    const lineDataSlice = lineData.data.slice()
                    lineData.data = reorderedIndices.map((i) => lineDataSlice[i])
                    const labelsSlice = sortedLabels.slice()
                    sortedLabels = reorderedIndices.map((i) => labelsSlice[i])
                }
            }

            formattedData = {
                fData: sortedData,
                lineData: [lineData],
                xAxisLabels: sortedLabels,
            }
        } else {
            formattedData = {
                fData: data,
                xAxisLabels: [],
            }
        }

        const allSeriesEmpty = Array.isArray(formattedData?.fData)
            && formattedData.fData.length > 0
            && formattedData.fData.every((series) => Array.isArray(series?.data) && series.data.length === 0)

        if (!formattedData || !formattedData.fData || R.isEmpty(formattedData.fData) || allSeriesEmpty) noData = true

        if (noData) return null

        const fillSeriesMergeFields = (name) => (
            typeof name === 'string'
                ? MergeFields.replace(name, this.appStore.locale, this.mergefields)
                : name
        )
        if (Array.isArray(formattedData.fData)) {
            formattedData.fData = formattedData.fData.map((series) => {
                if (!series) return series
                let name = fillSeriesMergeFields(series.name)

                if (series.reportingValue != null && typeof name === 'string') {
                    name = `${name} (${series.reportingValue})`
                }
                return { ...series, name }
            })
        }
        if (Array.isArray(formattedData.lineData)) {
            formattedData.lineData = formattedData.lineData.map((ld) => (
                ld ? { ...ld, name: fillSeriesMergeFields(ld.name) } : ld
            ))
        }

        const productIdSuffixes = new Set(
            (Array.isArray(data) ? data : [])
                .map((d) => d?.productId)
                .filter((p) => p != null)
                .map(String),
        )
        const stripProductSuffix = (label) => {
            if (typeof label !== 'string') return label
            const dashIndex = label.lastIndexOf('-')
            if (dashIndex === -1) return label
            const tail = label.slice(dashIndex + 1).trim()
            return (tail === '' || productIdSuffixes.has(tail))
                ? label.slice(0, dashIndex).replace(/\s+$/, '')
                : label
        }
        if (Array.isArray(formattedData.xAxisLabels)) {
            formattedData.xAxisLabels = formattedData.xAxisLabels.map(stripProductSuffix)
        }

        const ORDERED_SERIES_CHART_TYPES = ['standardColumn', 'standardBar', 'kpiChart', 'npsChart']
        const BENCHMARK_SERIES_ORDER = ['industry', 'sector', 'company', 'division', 'portfolio']
        if (ORDERED_SERIES_CHART_TYPES.includes(element.chartType)
            && !['crossbreak', 'inherited'].includes(element.kpiType)
            && Array.isArray(formattedData.fData)) {
            const benchmarkRank = (series) => {
                const raw = series?.benchmark || series?.benchmarkName
                const key = raw ? String(raw).toLowerCase() : null
                const idx = key ? BENCHMARK_SERIES_ORDER.indexOf(key) : -1
                return idx === -1 ? BENCHMARK_SERIES_ORDER.length : idx
            }
            formattedData.fData = formattedData.fData
                .map((series, index) => ({ series, index }))
                .sort((a, b) => (
                    benchmarkRank(a.series) - benchmarkRank(b.series) || a.index - b.index
                ))
                .map(({ series }) => series)
        }

        const { fData, xAxisLabels } = formattedData
        const currency = R.pathOr('$', ['widgetRenderSettings', 'currency'], element)
        let format = R.pathOr(element.isCurrency ? 'currency' : undefined,
            ['widgetRenderSettings', 'format'], element)
        let dataFormat = format === 'currency' ? this.getCurrencySymbol(currency) : format

        if (settings.showAverage) format = 'numeric'
        if (settings.showAverage) dataFormat = 'numeric'

        const xAxisRenderLabels = xAxisLabels && Array.isArray(xAxisLabels)
            ? xAxisLabels.map((l) => R.pathOr(l, ['widgetRenderSettings', 'xAxis', l], element))
            : xAxisLabels

        const effectiveElement = settings.showAverage ? {
            ...element,
            surveyYear: survey.year,
            widgetRenderSettings: {
                ...element.widgetRenderSettings,
                dataDecimals: element.chartType === 'npsChart' ? 0 : 2,
                format: 'numeric',
            },
        } : {
            ...element,
            surveyYear: survey?.year,
        }

        const averageColour = (avg) => {
            if (avg > 4.1) return '#DEF0E0'
            if (avg <= 4.1 && avg >= 3.9) return '#ECF4DE'
            if (avg < 3.9 && avg >= 3.6) return '#FEFBDF'
            return '#F8E0E1'
        }

        switch (element.chartType) {
            case 'npsChart':
            case 'kpiChart':
            case 'standardBar':
            case 'standardColumn': {
                const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], effectiveElement)
                const renderSettings = chartRenderSettings(effectiveElement, this, xAxisLabels, fData)
                if (['crossbreak', 'inherited'].includes(element.kpiType)) {
                    formattedData.lineData.forEach((ld, i) => (
                        renderSettings.layoutEdit.push({
                            path: ['series', i + 1],
                            value: {
                                data: ld.data.map((d) => ({ value: d.value, name: ld.name, year: undefined, benchmark: ld.name, benchmarkName: ld.name })),
                                emphasis: { focus: 'series' },
                                type: 'line',
                                yAxisIndex: element.kpiType === 'crossbreak' ? 0 : 1,
                                ...(element.kpiType === 'inherited'
                                    ? { itemStyle: { color: '#3C4696' }, lineStyle: { color: '#3C4696' } }
                                    : {}),
                                label: {
                                    show: element.kpiType === 'inherited',
                                    position: 'top',
                                    formatter: (params) => {
                                        if (params?.data?.value == null) return ''
                                        const value = Number(params.data.value)
                                        if (!Number.isFinite(value)) return ''
                                        return value.toFixed(2)
                                    },
                                    borderColor: '#000000',
                                    borderWidth: 1,
                                    padding: [2, 4],
                                    fontSize: 8,
                                    backgroundColor: '#FFFFFF',
                                    color: '#000000',
                                },
                                name: ld.name,
                                stack: undefined,
                            },
                        })
                    ))
                    renderSettings.layoutEdit.push({
                        path: ['legend', 'data'],
                        value: [
                            { name: fData[0].name },
                            ...formattedData.lineData.map((ld) => ({ name: ld.name })),
                        ],
                    })
                }

                if (element.kpiType === 'inherited') {
                    renderSettings.layoutEdit.push({
                        path: ['yAxis', 0],
                        value: {
                            type: 'value',
                            min: 0,
                            max: 100,
                            interval: 10,
                            axisLabel: {
                                formatter: '{value}%',
                            },
                            splitLine: {
                                show: false,
                            },
                        },
                    })
                    // const lineValues = R.pathOr([], ['lineData'], formattedData)
                    //     .flatMap((ld) => R.pathOr([], ['data'], ld).map((d) => Number(d.value)))
                    //     .filter((v) => Number.isFinite(v))
                    // const lineMin = lineValues.length ? Math.min(0, Math.floor(Math.min(...lineValues))) : 0
                    // const lineMax = lineValues.length ? Math.max(5, Math.ceil(Math.max(...lineValues))) : 5
                    // const lineInterval = Math.max(1, Math.ceil((lineMax - lineMin) / 6))

                    renderSettings.layoutEdit.push({
                        path: ['yAxis', 1],
                        value: {
                            type: 'value',
                            splitLine: {
                                show: false,
                            },
                            // min: lineMin,
                            // max: lineMax,
                            // interval: lineInterval,
                            axisLabel: {
                                formatter: '{value}',
                            },
                        },
                    })
                }
                if (element.kpiType === 'crossbreak') {
                    renderSettings.layoutEdit.push({
                        path: ['yAxis', 0],
                        value: {
                            type: 'value',
                            splitLine: {
                                show: false,
                            },
                            axisLabel: {
                                fontSize: 12,
                            },
                        },
                    })
                    renderSettings.layoutEdit.push({
                        path: ['yAxis', 1],
                        value: {
                            type: 'value',
                            splitLine: {
                                show: false,
                            },
                            axisLabel: {
                                fontSize: 12,
                                show: false,
                            },
                        },
                    })
                }
                if (element.chartType === 'npsChart') {
                    // Shade the NPS score bands (detractors / passives / promoters).
                    // Only on 1x1 slides, where there's room for the bands to read clearly.
                    if (this.activeSlide?.template === '1x1') {
                        renderSettings.layoutEdit.push({
                            path: ['series', 0, 'markArea'],
                            value: {
                                silent: true,
                                data: [
                                    [
                                        { xAxis: 0, itemStyle: { color: 'rgba(255, 0, 0, 0.15)' } },
                                        { xAxis: 6 },
                                    ],
                                    [
                                        { xAxis: 7, itemStyle: { color: 'rgba(255, 200, 0, 0.18)' } },
                                        { xAxis: 8 },
                                    ],
                                    [
                                        { xAxis: 9, itemStyle: { color: 'rgba(0, 180, 0, 0.15)' } },
                                        { xAxis: 10 },
                                    ],
                                ],
                            },
                        })
                    }

                    // Hide data labels once there are 5+ series, to avoid clutter.
                    if (fData.length >= 5 && renderSettings.defaultSeries?.label) {
                        renderSettings.defaultSeries.label.show = false
                    }
                }
                const chart = this.activeSlide.question.get(this.currentChart ?? element.position)
                const qBench = this.questionBenchmarks.get(element.surveyId)
                let stackBench = null
                if (R.pathOr(null, [this.activePeriodKey, 'benchmarks'], qBench)) stackBench = qBench[this.activePeriodKey]?.benchmarks[element.questionId]
                // Only show benchmarks that actually have data present for the key.
                const order = ['industry', 'sector', 'company', 'division', 'portfolio']
                    .filter((key) => stackBench?.[key])

                // fData is in the chart's SORTED display order, so map each display
                // position to its option id (matching the data point label back to
                // the raw options). Highlight indexes use this sorted order.
                const optionIdByLabel = {}
                const optionEntries = effectiveElement.options ?? []
                optionEntries.forEach(([optId, label]) => {
                    optionIdByLabel[label] = optId
                    optionIdByLabel[stripOutHtml(label)] = optId
                })
                const sortedOptionIds = R.pathOr([], [0, 'data'], fData).map((d) => optionIdByLabel[d?.name])

                // With a single dataset each category has just one bar, so highlight
                // the whole option area with a grey background rather than a border.
                // Multiple datasets keep the border so the specific bar is clear.
                const singleDataset = fData.length === 1

                return (
                    <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden',  }}>
                        {['kpiChart', 'npsChart'].includes(element.chartType) && (
                            <>
                                {this.activeSlide.template !== '1x1' ? (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 10, marginRight: 10 }}>
                                        <div style={{ display: 'flex', textAlign: 'center', flexDirection: 'column', border: '1px solid black', minWidth: 70 }}>
                                            <div style={{ height: 20, borderBottom: '1px solid black' }} />
                                            <div style={{ height: 20,fontSize: 10 }}>{element.chartType === 'npsChart' ? 'NPS' : 'Average'}</div>
                                        </div>

                                        {stackBench ? (
                                            order.filter((k) => chart.benchmark.includes(k)).map((key) => {
                                                const pillBg = colours.colorObj[key]
                                                return (
                                                    <div style={{ display: 'flex', textAlign: 'center', flexDirection: 'column', border: '1px solid black', minWidth: 70 }}>
                                                        <div style={{ height: 20, paddingInline: 5, paddingTop: 2, paddingBottom: 2, backgroundColor: pillBg, fontSize: 10, color: 'white', borderBottom: '1px solid black' }}>{capitalize(key)}</div>
                                                        <div style={{ height: 20, fontSize: 10 }}>{element.chartType === 'npsChart' ? Math.round(stackBench[key].nps) : stackBench[key].average.toFixed(2)}</div>
                                                    </div>
                                                )
                                            })
                                        ): null}

                                        {element.averages.fData.filter((y) => chart.comparisonYears.includes(y.name)).map((key) => {
                                            const pillBg = colours.colorObj[yearColourKey(key.name, survey.year)]
                                            return (
                                                <div style={{ fontSize: 10, display: 'flex', textAlign: 'center', flexDirection: 'column', border: '1px solid black', minWidth: 70 }}>
                                                    <div style={{ height: 20, paddingInline: 5, paddingTop: 2, paddingBottom: 2, backgroundColor: pillBg, fontSize: 10, color: 'white', borderBottom: '1px solid black' }}>{capitalize(key.name)}</div>
                                                    <div style={{ height: 20, fontSize: 10 }}>{element.chartType === 'npsChart' ? Math.round(key.data[0].value) : key.data[0].value.toFixed(2)}</div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'flex-end', marginTop: 10, marginRight: 10 }}>
                                        {stackBench ? (
                                            order.filter((k) => chart.benchmark.includes(k)).map((key) => {
                                                const pillBg = colours.colorObj[key]
                                                return (
                                                    <div style={{ display: 'flex', textAlign: 'center', flexDirection: 'column', padding: '5px 10px', borderRadius: 10, backgroundColor: '#EDF1F7', minWidth: 80 }}>
                                                        <div style={{ paddingInline: 5, paddingTop: 2, paddingBottom: 2, backgroundColor: pillBg, borderRadius: 100, fontSize: 8, color: 'white' }}>{capitalize(key)}</div>
                                                        <div style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 14 }}>{element.chartType === 'npsChart' ? Math.round(stackBench[key].nps) : stackBench[key].average.toFixed(2)}</div>
                                                    </div>
                                                )
                                            })
                                        ): null}

                                        {element.averages.fData.filter((y) => chart.comparisonYears.includes(y.name)).map((key) => {
                                            const pillBg = colours.colorObj[yearColourKey(key.name, survey.year)]
                                            return (
                                                <div style={{ fontSize: 10, display: 'flex', textAlign: 'center', flexDirection: 'column', padding: '5px 10px', borderRadius: 10, backgroundColor: '#EDF1F7', minWidth: 80 }}>
                                                    <div style={{ paddingInline: 5, paddingTop: 2, paddingBottom: 2, backgroundColor: pillBg, borderRadius: 100, fontSize: 8, color: 'white' }}>{capitalize(key.name)}</div>
                                                    <div style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 14 }}>{element.chartType === 'npsChart' ? Math.round(key.data[0].value) : key.data[0].value.toFixed(2)}</div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </>
                        )}

                        <Bar
                            xAxisLabels={renderSettings.finalxAxisLabels}
                            chartData={fData}
                            style={{ width: '100%', height: '100%' }}
                            transpose={element.chartType === 'standardBar'}
                            layoutEdit={[...renderSettings.layoutEdit]}
                            showLegend
                            dataFormat={dataFormat}
                            key={element.key}
                            tooltipDecimals={decimals}
                            yAxisDecimals={decimals}
                            highlightMode={singleDataset ? 'background' : 'border'}
                            initialHighlighted={chart?.highlighted?.map((opt) => {
                                return { ...opt, index: sortedOptionIds.indexOf(opt.index) }
                            })}
                            highlightable={!['inherited', 'crossbreak', 'OSS'].includes(effectiveElement.kpiType) && !['kpiChart', 'npsChart'].includes(effectiveElement.chartType)}
                            onHighlightChange={(options) => this.handleHighlightOptions(options, chart, sortedOptionIds)}
                            defaultSeries={renderSettings.defaultSeries || {
                                type: 'bar',
                                label: {
                                    show: false,
                                },
                                emphasis: {
                                    focus: 'none',
                                },
                                data: [],
                            }}
                        />
                    </div>
                )
            }
            case 'stackedBar':
            case 'stackedColumn': {
                const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], effectiveElement)

                const isPctData = R.pathOr('', ['dataDetails', 'type'], element) === 'pct'
                const stackedData = isPctData ? normalizeStackedData(fData) : fData

                const stackedElement = isPctData ? { ...effectiveElement, max: 100 } : effectiveElement

                const renderSettings = chartRenderSettings(stackedElement, this, xAxisLabels, stackedData)
                const qBench = this.questionBenchmarks.get(stackedElement.surveyId)
                let stackBench = null
                if (R.pathOr(null, [this.activePeriodKey, 'benchmarks'], qBench)) stackBench = qBench[this.activePeriodKey]?.benchmarks[stackedElement.questionId]
                const optionMap = new Map()
                stackedElement.options.forEach((ops) => optionMap.set(stripOutHtml(ops[1]), ops[0]))
                // highlightOrder maps a chart category index -> optionId in the
                // chart's data order (matches params.dataIndex on click). `order`
                // is the same list reversed for stackedBar to align the side
                // panel, so keep them separate.
                const highlightOrder = stackedData[0].data.map((d) => d.name).map((name) => optionMap.get(name))
                const order = [...highlightOrder]
                if (element.chartType === 'stackedBar') order.reverse()
                const chart = this.activeSlide.question.get(this.currentChart ?? stackedElement.position)
                const hasZeroToFiveScale = Object.values(stackedElement.optionReportingValues).includes(-2)
                return (
                    <div style={{ display: 'flex', height: '100%' }}>
                        <Bar
                            xAxisLabels={xAxisRenderLabels}
                            chartData={stackedData}
                            style={{ width: '100%', height: '100%' }}
                            transpose={element.chartType === 'stackedBar'}
                            showLegend
                            layoutEdit={[...renderSettings.layoutEdit]}
                            tooltipDecimals={decimals}
                            yAxisDecimals={decimals}
                            dataFormat={dataFormat}
                            key={element.key}
                            highlightMode="background"
                            highlightable={!['inherited', 'crossbreak', 'OSS'].includes(effectiveElement.kpiType)}
                            initialHighlighted={chart?.highlighted?.map((opt) => (
                                { ...opt, index: highlightOrder.indexOf(opt.index) }
                            ))}
                            onHighlightChange={(options) => this.handleHighlightOptions(options, chart, highlightOrder)}
                            defaultSeries={renderSettings.defaultSeries || {
                                name: '',
                                type: 'bar',
                                stack: 'data',
                                label: {
                                    show: false,
                                },
                                emphasis: {
                                    focus: 'none',
                                },
                                data: [],
                            }}
                        />
                        <div style={{ display: 'flex', padding: 10, paddingBottom: 50 }}>
                            {stackBench ? (
                                Object.keys(stackBench).filter((k) => chart.benchmark.includes(k)).map((key) => (
                                    <div style={{ fontSize: 10, display: 'flex', textAlign: 'center', flexDirection: 'column', minWidth: 52 }}>
                                        <div style={{ paddingInline: 5 }}>{capitalize(key)}</div>
                                        {order.map((oid) => (
                                            <div style={{ fontWeight: 'bold', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid', backgroundColor: hasZeroToFiveScale ? 'white' : averageColour(stackBench[key].values[oid].toFixed(2)) }}>{stackBench[key].values[oid].toFixed(2)}</div>
                                        ))}
                                    </div>
                                ))
                            ): null}
                            {console.log('stackedElement.averages', stackedElement.averages, order)}
                            {Object.keys(stackedElement.averages).filter((y) => chart.comparisonYears.includes(parseInt(y))).map((key) => (
                                <div style={{ fontSize: 10, display: 'flex', textAlign: 'center', flexDirection: 'column', minWidth: 52 }}>
                                    <div style={{ paddingInline: 5 }}>{capitalize(key)}</div>
                                    {order.map((oid) => (
                                        <div style={{ fontWeight: 'bold', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid', backgroundColor: hasZeroToFiveScale ? 'white' : averageColour(R.pathOr(0, [key, oid], stackedElement.averages).toFixed(2)) }}>{R.pathOr(0, [key, oid], stackedElement.averages).toFixed(2)}</div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                )
            }
            case 'inheritedStackedBar': {
                const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], effectiveElement)
                const renderSettings = chartRenderSettings(effectiveElement, this, xAxisLabels, fData)
                const hasZeroToFiveScale = Object.values(effectiveElement.optionReportingValues).includes(-2)
                const chart = this.activeSlide.question.get(this.currentChart ?? element.position)
                // Build a score column per comparison year: averages is [{ year, data: { optId: value } }],
                // so join it with the options to key values by option label (matching the chart rows).
                const comparisonYearSet = new Set((chart?.comparisonYears ?? []).map(Number))
                const scoreColumns = (effectiveElement.averages ?? [])
                    .filter((yearEntry) => comparisonYearSet.has(Number(yearEntry.year)))
                    .slice()
                    .sort((a, b) => Number(a.year) - Number(b.year))
                    .map((yearEntry) => {
                        return{
                        year: yearEntry.year,
                        valueByLabel: (effectiveElement.options ?? []).reduce((acc, [optId, label]) => {
                            const value = yearEntry.data?.[optId]
                                ?? yearEntry.data?.[optId?.toUpperCase?.()]
                                ?? yearEntry.data?.[optId?.toLowerCase?.()]
                            acc[label] = value
                            acc[stripOutHtml(label)] = value
                            return acc
                        }, {}),
                    }})
                return (
                    <div style={{ display: 'flex', height: '100%' }}>
                        <Bar
                            xAxisLabels={xAxisRenderLabels}
                            chartData={fData}
                            style={{ width: '100%', height: '100%', paddingTop: 20 }}
                            transpose
                            layoutEdit={[...renderSettings.layoutEdit]}
                            dataFormat={dataFormat}
                            key={element.key}
                            tooltipDecimals={decimals}
                            yAxisDecimals={decimals}
                            defaultSeries={{
                                type: 'bar',
                                showBackground: true,
                                backgroundStyle: { color: '#EDF1F7', borderRadius: 4 },
                                itemStyle: { borderRadius: 4 },
                                label: { show: false },
                                emphasis: { focus: 'none' },
                                data: [],
                            }}
                        />

                        <div style={{ display: 'flex', padding: '10px 0px', flexShrink: 0, paddingRight: 20 }}>
                            <div style={{ fontSize: 10, display: 'flex', flexDirection: 'column' }}>
                                <div style={{ height: 20, fontSize: 10, fontWeight: 'bold', flexShrink: 0, minWidth: 52, padding: '0px 5px' }}>% Held</div>
                                {R.pathOr([], [0, 'data'], fData).slice().map((point) => (
                                    <div style={{ fontWeight: 'bold', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid black' }}>
                                        {`${Math.round(Number(point?.value) || 0)}%`}
                                    </div>
                                ))}
                            </div>
                        
                            {scoreColumns.map((column) => (
                                <div key={column.year} style={{ fontSize: 10, display: 'flex', textAlign: 'center', flexDirection: 'column', minWidth: 52, flexShrink: 0 }}>
                                    <div style={{ height: 20, fontSize: 10, fontWeight: 'bold', flexShrink: 0, padding: '0px 5px' }}>{`${column.year}`} Score</div>
                                    {R.pathOr([], [0, 'data'], fData).slice().map((point) => {
                                        const value = Number(column.valueByLabel[point?.name]) || 0

                                        return (
                                            <div style={{ backgroundColor: hasZeroToFiveScale ? 'white' : averageColour(value), fontWeight: 'bold', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid black' }}>
                                                {value.toFixed(2)}
                                            </div>
                                        )
                                    })}
                                </div>
                            ))}

                            {scoreColumns.length === 2 && (
                                <div style={{ fontSize: 10, display: 'flex', textAlign: 'center', flexDirection: 'column', minWidth: 52, flexShrink: 0 }}>
                                    <div style={{ height: 20, fontSize: 10, fontWeight: 'bold', flexShrink: 0, padding: '0px 5px' }}>Change</div>
                                    {R.pathOr([], [0, 'data'], fData).slice().map((point) => {
                                        const earlier = Number(scoreColumns[0].valueByLabel[point?.name]) || 0
                                        const higher = Number(scoreColumns[1].valueByLabel[point?.name]) || 0

                                        return (
                                            <div style={{ fontWeight: 'bold', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid black' }}>
                                                {(higher - earlier).toFixed(1)}
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )
            }
            case 'line': {
                const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], effectiveElement)

                const renderSettings = chartRenderSettings(effectiveElement, this, xAxisLabels, fData)
                return (
                    <Line
                        xAxisLabels={xAxisRenderLabels}
                        chartData={fData}
                        style={{ width: '100%', height: '100%' }}
                        showLegend
                        layoutEdit={[...renderSettings.layoutEdit]}
                        tooltipDecimals={decimals}
                        yAxisDecimals={decimals}
                        dataFormat={dataFormat}
                        key={element.key}
                        defaultSeries={renderSettings.defaultSeries || undefined}
                    />
                )
            }
            case 'lmx': {
                const sortedData = sortBy(fData, 'year', -1).sort((a, b) => {
                    if (a.benchmark && !b.benchmark) return 1
                    if (!a.benchmark && b.benchmark) return -1
                    return 0
                })
                if (element.benchmarkType === 'YOY') {
                    const latestSelectedYear = R.pathOr(0, [0, 'year'], sortBy(
                        [...this.years].filter(([_, value]) => !!value)
                            .map(([key]) => ({ year: key })),
                        'year',
                        -1,
                    ))

                    const dataLatestYear = R.pathOr(
                        R.pathOr(0, [0, 'data', 0, 'year'], sortedData),
                        [0, 'year'],
                        sortedData,
                    )

                    if (latestSelectedYear !== dataLatestYear) {
                        sortedData.unshift({
                            year: latestSelectedYear,
                            data: [
                                { value: '', name: 'No data available' },
                                { value: '', name: 'No data available' },
                                { value: '', name: 'No data available' },
                                { value: '', name: 'No data available' },
                            ],
                        })
                    }
                }

                let dataWithBenchmarks = []
                const benchData = this.lmxBenchmarkData.get(`${element.surveyId}-LMX`)

                if (benchData) {
                    // Use the benchmarks selected on this chart (via the LMX settings modal).
                    const selectedBenchmarkValues = new Set(element.benchmark ?? [])
                    Object.entries(benchData).forEach(([scope, bd]) => {
                        if (!selectedBenchmarkValues.has(scope)) return
                        if (!bd[this.benchmarkPeriod]) return

                        sortedData.push({
                            data: Object.entries(bd[this.benchmarkPeriod])
                                .map(([key, value]) => ({ value: Math.round(value), name: `${key}`, tagOptionId: null })),
                            benchmarkName: capitalize(scope),
                            benchmarkPeriod: this.benchmarkPeriod,
                        })
                    })
                }

                const selectedComparisonYears = new Set((element.comparisonYears ?? []).map(Number))
                const remainingRows = R.remove(0, 1, R.or(sortedData, [0])).reverse()
                const benchmarkRows = remainingRows.filter((d) => d.benchmarkName)
                const comparisonYearRows = remainingRows
                    .filter((d) => !d.benchmarkName && selectedComparisonYears.has(Number(d.year)))

                const buildOverlay = (rows, d) => rows.slice(0, 6).map((bd) => ({
                    name: `${bd.benchmarkName || bd.year}:`,
                    value: (
                        <span className="benchmarkValue">
                            {bd.data.find((da) => da.name === d.name)?.value}
                            %
                        </span>
                    ),
                }))

                if (benchmarkRows.length || comparisonYearRows.length) {
                    dataWithBenchmarks = R.pathOr([], [0, 'data'], sortedData).map((d) => ({
                        ...d,
                        value: d.value ? `${Number(d.value)}%` : '',
                        benchmarks: buildOverlay(benchmarkRows, d),
                        comparisonYears: buildOverlay(comparisonYearRows, d),
                    }))
                } else {
                    dataWithBenchmarks = R.pathOr([], [0, 'data'], sortedData)
                        .map((d) => ({ ...d, value: d.value ? `${Number(d.value)}%` : '' }))
                }
                return (
                    <LMX
                        chartData={dataWithBenchmarks.map((d) => ({
                            ...d,
                            name: startCase(d.name),
                        }))}
                        style={{ width: '100%', height: '100%' }}
                        key={element.key}
                        matrixAxisNames={R.pathOr({}, ['widgetRenderSettings', 'matrixAxisNames'], element)}
                        containerSize={element.containerSize}
                    />
                )
            }
            case 'quadrantAnalysis': {
                const renderSettings = chartRenderSettings(element, this, xAxisLabels)
                const dataFormatObj = {
                    x: R.pathOr(false, ['quadrantCurrency', 'xAxis'], element) ? dataFormat : undefined,
                    y: R.pathOr(false, ['quadrantCurrency', 'yAxis'], element) ? dataFormat : undefined,
                }

                return (
                    <QuadrantAnalysis
                        chartData={fData}
                        style={{ width: '100%', height: '100%' }}
                        showLabels={false}
                        dataFormat={dataFormatObj}
                        layoutEdit={[...renderSettings.layoutEdit]}
                        key={element.key}
                    />
                )
            }
            case 'doughnut':
            case 'pie': {
                const pieElement = {
                    ...element,
                    widgetRenderSettings: {
                        ...element.widgetRenderSettings,
                        showLegend: false,
                        showDataLabels: false,
                        showOptionLabels: true,
                        labelPosition: 'outside',
                    },
                }
                const renderSettings = chartRenderSettings(pieElement, this, xAxisLabels)
                const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], effectiveElement)
                const labelPrefix = dataFormat && dataFormat !== 'numeric' && dataFormat !== '%' ? dataFormat : ''
                const labelSuffix = dataFormat === '%' ? '%' : ''
                renderSettings.layoutEdit.push(
                    {
                        path: ['series', 0, 'label'],
                        value: {
                            show: true,
                            position: 'outside',
                            fontSize: 12,
                            fontWeight: 700,
                            formatter: (params) => {
                                const valueLabel = `${labelPrefix}${Number(params.value).toFixed(decimals)}${labelSuffix}`
                                return params.name ? `${valueLabel} - ${params.name}` : valueLabel
                            },
                        },
                    },
                    {
                        path: ['series', 0, 'labelLine', 'show'],
                        value: true,
                    },
                )
                const slices = fData.find(({ name }) => name === survey.year)?.data
                    ?? R.pathOr([], [fData.length - 1, 'data'], fData)
                return (
                    <Pie
                        chartData={slices}
                        donut={element.chartType === 'doughnut'}
                        dataFormat={dataFormat}
                        tooltipDecimals={decimals}
                        layoutEdit={[...renderSettings.layoutEdit]}
                        style={{ width: '100%', height: '100%' }}
                        key={element.key}
                    />
                )
            }
            default:
                return null
        }
    }

    getChartDefaults = (question, hasNumericCodes, hasOtherOrExclusive) => {
        const { questionTypeCode, metadata: { kpiType } } = question

        const chartType = this.chartTypesForQuestion({ questionTypeCode, kpiType, hasNumericCodes })[0]?.value

        switch (questionTypeCode) {
            case 'RD':
            case 'SB':
                return {
                    chartType,
                    sorting: (!hasNumericCodes && question?.optionSet.options?.length >= 6) ? 'highToLow' : 'noRanking',
                    removeBaseLimit: 0,
                    removeZeroNull: question?.optionSet.options?.length >= 6 && question?.numeric ? 'zero' : 'none',
                    removeZero: (!hasNumericCodes && question?.optionSet.options?.length >= 6),
                    excludeNonNumeric: null,
                    lock: (hasOtherOrExclusive && !hasNumericCodes && question?.optionSet.options?.length >= 6),
                    kpiType,
                }
            case 'CH':
                return {
                    chartType,
                    sorting: 'highToLow',
                    removeBaseLimit: 0,
                    removeZero: true,
                    removeZeroNull: 'zero',
                    excludeNonNumeric: null,
                    lock: hasOtherOrExclusive,
                }
            case 'TB':
                return {
                    chartType,
                    sorting: question?.numeric ? 'n/a' : 'highToLow',
                    removeBaseLimit: 0,
                    removeZero: true,
                    removeZeroNull: 'zero',
                    excludeNonNumeric: question?.numeric ? null : true,
                    lock: null,
                }
            case 'OD':
                return {
                    chartType,
                    sorting: 'lowToHigh',
                    removeBaseLimit: 0,
                    removeZeroNull: 'zero',
                    excludeNonNumeric: null,
                    lock: null,
                }
            case 'TA':
            case 'TX':
                return {
                    chartType,
                    sorting: 'none',
                }
            default:
                return {}
        }
    }
}
